/**
 * Bitcoin Mining Calculator v2
 * IIFE pattern — basic mining profitability calculator with currency formatting.
 */
(function() {
  'use strict';

  var btcPrice = 0;
  var liveBtcPrice = 0;
  var difficulty = 0;
  var networkHashrateTH = 0;
  var tickerData = null;

  // Historical data state
  var historicalDifficultyData = null;
  var historicalPriceDataUSD = null;
  var historicalDataReady = false;

  // 4-year data for averages (price + difficulty)
  var fourYearPriceDataUSD = null;
  var fourYearDifficultyData = null;
  var fourYearAvgUSD = 0;
  var oneYearAvgUSD = 0;
  var fourYearAvgDifficulty = 0;
  var oneYearAvgDifficulty = 0;
  var avgDataReady = false;

  // Profitability tab state
  var compPriceMode = 'avg4y'; // 'avg4y', 'avg1y', 'current'
  var forecastEnabled = true;
  var compTimeframeYears = 4;
  var compActiveView = 'heaters'; // 'heaters', 'miners'
  var heaterSunshineHours = 2;
  var minerSunshineHours = 4;

  var BLOCKS_PER_DAY = 144;
  var BLOCK_REWARD = 3.125;

  // Halving schedule: approximate dates and the NEW reward after each halving
  var HALVING_SCHEDULE = [
    { year: 2028, month: 3, reward: 1.5625 },    // ~April 2028
    { year: 2032, month: 3, reward: 0.78125 },   // ~April 2032
    { year: 2036, month: 3, reward: 0.390625 },  // ~April 2036
    { year: 2040, month: 3, reward: 0.1953125 }  // ~April 2040
  ];

  // Active currency state (overrides Shopify globals when user switches)
  var activeCurrency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || window.shopifyCurrencyCode || 'EUR';
  var activeCurrencySymbol = window.shopifyCurrencySymbol || '\u20AC';
  var activeMoneyFormat = window.shopifyMoneyFormat;

  // Currency config lookup
  var currencyConfig = {
    EUR: { symbol: '\u20AC', format: '\u20AC{{amount_with_comma_separator}}' },
    USD: { symbol: '$', format: '${{amount}}' }
  };

  // DOM refs
  var elPriceInput = document.getElementById('bmcBtcPriceInput');
  var elPriceBadge = document.getElementById('bmcPriceBadge');
  var elPriceBadgeText = document.getElementById('bmcPriceBadgeText');
  var elPriceReset = document.getElementById('bmcPriceReset');
  var elDevice = document.getElementById('bmcDevice');
  var elHashrate = document.getElementById('bmcHashrate');
  var elPower = document.getElementById('bmcPower');
  var elElectricity = document.getElementById('bmcElectricity');
  var elPoolFee = document.getElementById('bmcPoolFee');
  var elElectricitySuffix = document.getElementById('bmcElectricitySuffix');
  var elPriceSuffix = document.getElementById('bmcPriceSuffix');
  var elCurrencySelect = document.getElementById('bmcCurrencySelect');
  var elDisclaimer = document.getElementById('bmcDisclaimer');

  // Mining tab PV toggle DOM refs
  var elPvToggle = document.getElementById('bmcPvToggle');
  var elPvGroup = document.getElementById('bmcPvGroup');
  var elPvSize = document.getElementById('bmcPvSize');
  var elPvSunshine = document.getElementById('bmcPvSunshine');
  var elPvAllocation = document.getElementById('bmcPvAllocation');
  var elFeedInTariff = document.getElementById('bmcFeedInTariff');
  var elFeedInSuffix = document.getElementById('bmcFeedInSuffix');

  // Translated labels for disclaimer
  var labels = window.bmc2Labels || {};

  // Format number with locale-aware separators
  function formatNumber(n) {
    return n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  }

  // Format break-even months into readable text (e.g. "3 years 6 months")
  function formatBreakeven(months) {
    var y = Math.floor(months / 12);
    var m = months % 12;
    if (y === 0) return m + ' ' + (m === 1 ? (labels.breakeven_month || 'month') : (labels.breakeven_months || 'months'));
    var text = y + ' ' + (y === 1 ? (labels.breakeven_year || 'year') : (labels.breakeven_years || 'years'));
    if (m > 0) text += ' ' + m + ' ' + (m === 1 ? (labels.breakeven_month || 'month') : (labels.breakeven_months || 'months'));
    return text;
  }

  // Build pills (break-even + ROI) with info tooltip showing real numbers
  var pillTooltipCounter = 0;
  function buildPills(r, timeframeYears) {
    var breakeven = r.breakeven;
    var roi = r.roi;
    var totalPrice = r.totalPrice;
    var totalEarnings = r.totalEarnings;
    var tid = totalPrice > 0 ? 'bmcPillTip' + (pillTooltipCounter++) : '';

    var html = '<div class="bmc-heater-card__pills">';
    if (breakeven > 0) {
      html += '<span class="bmc-heater-card__pill">' + (labels.break_even || 'Break-even') + ': ' + formatBreakeven(breakeven) + '</span>';
    }
    if (totalPrice > 0) {
      html += '<span class="bmc-heater-card__pill">' + timeframeYears + (labels.years_suffix || 'Y') + ' ROI: ' + (roi >= 0 ? '+' : '') + roi.toFixed(0) + '%</span>';
      // Info icon + tooltip in a relative wrapper so tooltip positions relative to icon
      html += '<span class="bmc-heater-card__pills-info-wrap">';
      html += '<button type="button" class="bmc-calc__info-btn" aria-label="Info" data-tooltip="' + tid + '">';
      html += '<svg width="16" height="16" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="7.5" stroke="currentColor"/><path d="M8 7v4M8 5h.005" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>';
      html += '</button>';
      html += '<div class="bmc-calc__tooltip bmc-heater-card__pills-tooltip" id="' + tid + '" role="tooltip" hidden>';
      var annualEarnings = totalEarnings / timeframeYears;
      var monthlyEarnings = annualEarnings / 12;
      if (breakeven > 0 && monthlyEarnings > 0) {
        html += '<span><strong>' + (labels.break_even || 'Break-even') + ':</strong> ' + formatMoney(totalPrice) + ' / (' + formatMoney(totalEarnings) + ' / ' + timeframeYears + ' / 12) = ' + formatBreakeven(breakeven) + '</span>';
      }
      html += '<span><strong>ROI:</strong> (' + formatMoney(totalEarnings) + ' - ' + formatMoney(totalPrice) + ') / ' + formatMoney(totalPrice) + ' = ' + (roi >= 0 ? '+' : '') + roi.toFixed(0) + '%</span>';
      html += '</div>';
      html += '</span>';
    }
    html += '</div>';
    return html;
  }

  // Format money using Shopify's built-in formatMoney with active currency state
  function formatMoney(amount) {
    var cents = Math.round(amount * 100);

    if (window.Shopify && window.Shopify.formatMoney) {
      var formatted = window.Shopify.formatMoney(cents, activeMoneyFormat);
      if (formatted && formatted.trim().length > 0) {
        if (window.checkForOverrides) {
          return window.checkForOverrides(formatted, activeCurrency, activeCurrencySymbol);
        }
        return formatted;
      }
    }

    // Fallback
    var abs = Math.abs(amount);
    var sign = amount < 0 ? '-' : '';
    return sign + activeCurrencySymbol + abs.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  // Get BTC price in active currency from ticker data
  function getBtcPriceFromTicker(data) {
    // Direct match in ticker response
    if (data[activeCurrency] && data[activeCurrency].last) {
      return data[activeCurrency].last;
    }

    // Fallback: EUR price × Shopify currency rate
    if (data['EUR'] && data['EUR'].last) {
      var rate = (window.Shopify && window.Shopify.currency && window.Shopify.currency.rate) || 1;
      return data['EUR'].last * rate;
    }

    // Last resort: USD
    if (data['USD'] && data['USD'].last) {
      return data['USD'].last;
    }

    return 0;
  }

  // Convert a USD amount to the active currency using ticker data
  function convertUsdToActiveCurrency(usdAmount) {
    if (!tickerData) return usdAmount;

    if (activeCurrency === 'USD') return usdAmount;

    var usdLast = tickerData['USD'] && tickerData['USD'].last;
    var activeLast = tickerData[activeCurrency] && tickerData[activeCurrency].last;

    if (usdLast && activeLast) {
      return usdAmount * (activeLast / usdLast);
    }

    // Fallback: use EUR as intermediate
    var eurLast = tickerData['EUR'] && tickerData['EUR'].last;
    if (usdLast && eurLast) {
      var rate = (window.Shopify && window.Shopify.currency && window.Shopify.currency.rate) || 1;
      return usdAmount * (eurLast / usdLast) * rate;
    }

    return usdAmount;
  }

  // Update the electricity suffix with current currency symbol
  function updateCurrencySuffix() {
    elElectricitySuffix.textContent = activeCurrencySymbol + '/kWh';
    if (elPriceSuffix) elPriceSuffix.textContent = activeCurrencySymbol;
    if (elFeedInSuffix) elFeedInSuffix.textContent = activeCurrencySymbol + '/kWh';
  }

  // Switch active currency, update BTC price from ticker, and recalculate
  function switchCurrency(code) {
    var config = currencyConfig[code];
    if (!config) return;

    activeCurrency = code;
    activeCurrencySymbol = config.symbol;
    activeMoneyFormat = config.format;

    updateCurrencySuffix();

    // Re-derive BTC price from cached ticker data
    if (tickerData) {
      var price = getBtcPriceFromTicker(tickerData);
      liveBtcPrice = price;
      btcPrice = price;
      if (price > 0) {
        elPriceInput.value = formatPriceForInput(price);
        autoSizePriceInput();
        updatePriceBadge();
      }
    }

    // Sync all currency selects and price displays
    if (elCurrencySelect) elCurrencySelect.value = code;
    if (elCompCurrency) elCompCurrency.value = code;
    if (elCompBtcPrice && btcPrice > 0) {
      elCompBtcPrice.value = formatPriceForInput(btcPrice);
      autoSizeCompPriceInput();
      updateCompPriceBadge();
    }

    // Re-trigger price mode display refresh
    if (compPriceMode === 'avg4y' || compPriceMode === 'avg1y') {
      switchPriceMode(compPriceMode);
    }

    calculate();
    calculateComparison();
  }

  // Unified "How it's calculated" builder for both calculators
  function buildCalcDetails(opts) {
    var u = opts.used || {};
    var L = labels; // shorthand
    var h = '';

    // Helper: replace [placeholder] tokens in a translated string
    function tr(key, fallback, replacements) {
      var s = L[key] || fallback;
      if (replacements) {
        for (var k in replacements) {
          s = s.replace('[' + k + ']', replacements[k]);
        }
      }
      return s;
    }

    // ── Section 1: Your settings (only active items) ──
    h += '<span><strong>' + tr('hic_your_settings', 'Your settings') + '</strong></span>';

    h += '<span>' + tr('hic_btc_price', 'Bitcoin price') + ': ' + opts.btcPrice + '</span>';
    h += '<span>' + tr('hic_network', 'Network hashrate: [hashrate] EH/s · Difficulty: [difficulty] T', { hashrate: opts.hashrate, difficulty: opts.difficulty }) + '</span>';
    h += '<span>' + tr('hic_block_reward', 'Block reward: [reward] BTC/block', { reward: opts.blockReward }) + '</span>';
    if (u.timePeriod) h += '<span>' + tr('hic_time_period', 'Time period') + ': ' + opts.timePeriod + '</span>';
    if (u.minerSpecs) h += '<span>' + tr('hic_hashrate_power', 'Hashrate: [hashrate] TH/s · Power: [power] W', { hashrate: opts.minerHashrate, power: opts.minerPower }) + '</span>';

    h += '<span>' + tr('hic_electricity', 'Electricity cost: [cost] [symbol]/kWh', { cost: opts.electricityCost, symbol: opts.currSymbol }) + '</span>';
    h += '<span>' + tr('hic_pool_fee', 'Pool fee: [fee]%', { fee: opts.poolFee }) + '</span>';

    if (u.heating) {
      h += '<span>' + tr('hic_heating', 'Heating cost: [cost] [symbol]/kWh · Heat demand: [demand] kWh/yr', { cost: opts.heatingCost, symbol: opts.currSymbol, demand: opts.heatDemand }) + '</span>';
      h += '<span>' + tr('hic_heating_season', 'Heating season: [months] months ([days] days)', { months: opts.heatingMonths, days: opts.heatingDays }) + '</span>';
    }

    if (u.forecast) h += '<span>' + tr('hic_forecast', 'Forecast: BTC price +[btc]%/yr · Difficulty +[diff]%/yr', { btc: opts.btcGrowth, diff: opts.diffGrowth }) + '</span>';
    if (u.halvings) h += '<span>' + tr('hic_halvings', 'Halvings: included') + '</span>';
    if (u.hodl) h += '<span>' + tr('hic_hodl', 'Strategy: HODL (sell all BTC at end of period)') + '</span>';
    if (u.pv) h += '<span>' + tr('hic_pv', 'PV system: [size] kWp · [sun]h/day · Feed-in: [tariff] [symbol]/kWh', { size: opts.pvSize, sun: opts.pvSunshine, tariff: opts.feedInTariff, symbol: opts.currSymbol }) + '</span>';

    // ── Section 2: Step-by-step calculation ──
    if (u.timePeriod) {
      var stepNum = 1;

      h += '<span>&nbsp;</span>';
      h += '<span><strong>' + tr('hic_step_by_step', 'Step-by-step calculation') + '</strong></span>';

      // Step: Operating days
      h += '<span>&nbsp;</span>';
      h += '<span><strong>Step ' + stepNum + ': ' + tr('hic_step_operating_days', 'Operating days per year') + '</strong></span>';
      if (u.heating) {
        h += '<span>(' + opts.heatingMonths + ' / 12) x 365 = <strong>' + opts.heatingDays + '</strong></span>';
        h += '<span><em>' + tr('hic_operating_heating', 'Heaters only run during the heating season, so all calculations use [days] operating days per year.', { days: opts.heatingDays }) + '</em></span>';
      } else {
        h += '<span><strong>' + tr('hic_operating_365', '365 days/year (24/7 operation)') + '</strong></span>';
      }
      stepNum++;

      // Step: Daily energy consumption
      h += '<span>&nbsp;</span>';
      h += '<span><strong>Step ' + stepNum + ': ' + tr('hic_step_energy', 'Daily energy consumption per device') + '</strong></span>';
      h += '<span>' + tr('hic_energy_formula', '(power in watts / 1,000) x 24 hours = kWh/day') + '</span>';
      h += '<span><em>' + tr('hic_energy_note', 'Each product card uses its own wattage. Example: 1,000 W device = 24 kWh/day.') + '</em></span>';
      stepNum++;

      // Step: Heating savings
      if (u.heating) {
        h += '<span>&nbsp;</span>';
        h += '<span><strong>Step ' + stepNum + ': ' + tr('hic_step_heating', 'Heating savings') + '</strong></span>';
        h += '<span>a. kWh/' + (L.unit_year || 'yr') + ' = kWh/' + (L.unit_day || 'day') + ' x ' + opts.heatingDays + '</span>';
        h += '<span>b. min(' + opts.heatDemand + ', kWh/' + (L.unit_year || 'yr') + ')</span>';
        h += '<span>c. x ' + opts.heatingCost + ' ' + opts.currSymbol + '/kWh x ' + (opts.timePeriodYears || 1) + ' ' + (L.unit_years || 'years') + '</span>';
        h += '<span><em>' + tr('hic_heating_note', 'The device produces heat as a byproduct of mining. You save money by turning off your existing heating system. If the device produces more heat than your demand ([demand] kWh/yr), only the useful portion counts.', { demand: opts.heatDemand }) + '</em></span>';
        stepNum++;
      }

      // Step: PV savings
      if (u.pv) {
        h += '<span>&nbsp;</span>';
        h += '<span><strong>Step ' + stepNum + ': ' + tr('hic_step_pv', 'PV (solar) savings') + '</strong></span>';
        h += '<span>a. ' + opts.pvSize + ' kWp x ' + opts.pvSunshine + 'h x %</span>';
        h += '<span>b. min(PV, kWh/' + (L.unit_day || 'day') + ')</span>';
        h += '<span>c. (grid x ' + opts.electricityCost + ') + (PV x ' + opts.feedInTariff + ') ' + opts.currSymbol + '/kWh</span>';
        h += '<span><em>' + tr('hic_pv_note', 'Solar panels offset part of your electricity cost. You pay the feed-in tariff ([tariff] [symbol]/kWh) instead of the grid rate ([cost] [symbol]/kWh) for solar-covered kWh.', { tariff: opts.feedInTariff, symbol: opts.currSymbol, cost: opts.electricityCost }) + '</em></span>';
        stepNum++;
      }

      // Step: Year-by-year mining
      h += '<span>&nbsp;</span>';
      h += '<span><strong>Step ' + stepNum + ': ' + tr('hic_step_mining', 'Year-by-year mining (years 1 to [years])', { years: opts.timePeriodYears || 1 }) + '</strong></span>';
      h += '<span>' + tr('hic_mining_for_each', 'For each year:') + '</span>';

      if (u.forecast) {
        h += '<span>a. BTC = ' + opts.btcPrice + ' x (1 + ' + opts.btcGrowth + '%)^Y</span>';
        h += '<span>b. Hashrate = ' + opts.hashrate + ' EH/s x (1 + ' + opts.diffGrowth + '%)^Y</span>';
      } else {
        h += '<span>a. BTC = ' + opts.btcPrice + ' (' + tr('hic_mining_constant', 'constant') + ')</span>';
        h += '<span>b. Hashrate = ' + opts.hashrate + ' EH/s (' + tr('hic_mining_constant', 'constant') + ')</span>';
      }

      if (u.halvings) {
        h += '<span>c. Block reward: ' + opts.blockReward + ' → ' + (opts.blockReward / 2) + ' BTC (~Apr 2028)</span>';
      } else {
        h += '<span>c. Block reward = ' + opts.blockReward + ' BTC (' + tr('hic_mining_constant', 'constant') + ')</span>';
      }

      h += '<span>d. BTC/' + (L.unit_day || 'day') + ' = (TH/s / network TH/s) x 144 x reward</span>';
      h += '<span>e. ' + (L.mining_revenue || 'Revenue') + '/' + (L.unit_day || 'day') + ' = BTC x BTC price</span>';
      h += '<span>f. ' + (L.electricity_cost || 'Electricity') + '/' + (L.unit_day || 'day') + ' = kWh x ' + opts.electricityCost + ' ' + opts.currSymbol + '</span>';
      h += '<span>g. ' + (L.pool_fees || 'Pool fees') + '/' + (L.unit_day || 'day') + ' = revenue x ' + opts.poolFee + '%</span>';
      h += '<span>h. ' + (L.estimated_profit || 'Profit') + '/' + (L.unit_day || 'day') + ' = revenue - electricity - pool fees</span>';
      h += '<span>i. ' + (L.unit_year || 'Year') + ' = profit x ' + opts.operatingDays + ' ' + (L.unit_day || 'days') + '</span>';
      h += '<span>→ ' + (opts.timePeriodYears || 1) + ' ' + (L.unit_years || 'years') + ' = <strong>' + (L.comp_col_mining_earnings || 'mining earnings') + '</strong></span>';

      if (u.forecast) {
        h += '<span><em>' + tr('hic_mining_note_forecast', 'BTC price grows [btc]%/yr (more revenue), but difficulty grows [diff]%/yr (fewer BTC mined). The net result depends on which outpaces the other.', { btc: opts.btcGrowth, diff: opts.diffGrowth }) + '</em></span>';
      }
      if (u.halvings) {
        h += '<span><em>' + tr('hic_mining_note_halvings', 'At each halving (~every 4 years), the block reward is cut in half, significantly reducing BTC mined.') + '</em></span>';
      }
      stepNum++;

      // Step: HODL
      if (u.hodl) {
        h += '<span>&nbsp;</span>';
        h += '<span><strong>Step ' + stepNum + ': ' + tr('hic_step_hodl', 'HODL adjustment') + '</strong></span>';
        h += '<span>a. BTC = ' + opts.btcPrice + ' x (1 + ' + opts.btcGrowth + '%)^' + (opts.timePeriodYears || 1) + (opts.hodlPrice ? ' = ' + opts.hodlPrice : '') + '</span>';
        h += '<span>b. HODL = BTC mined x final price</span>';
        h += '<span>c. ' + (L.comp_col_mining_earnings || 'Mining earnings') + ' = HODL - ' + (L.electricity_cost || 'electricity') + (u.pv ? ' + PV' : '') + ' - ' + (L.pool_fees || 'pool fees') + '</span>';
        h += '<span><em>' + tr('hic_hodl_note', 'Instead of selling BTC as you mine, you hold everything and sell at the projected year-[years] price. Higher potential returns, but more risk.', { years: opts.timePeriodYears || 1 }) + '</em></span>';
        stepNum++;
      }

      // Step: Total earnings
      h += '<span>&nbsp;</span>';
      h += '<span><strong>Step ' + stepNum + ': ' + tr('hic_step_total', 'Total earnings') + '</strong></span>';
      if (u.heating) {
        h += '<span><strong>' + (L.comp_col_total_earnings || 'Total') + ' = ' + (L.comp_col_mining_earnings || 'mining earnings') + ' + ' + (L.comp_col_heating_savings || 'heating savings') + '</strong></span>';
        h += '<span><em>' + tr('hic_total_heating_note', 'Mining earnings (revenue minus costs) plus what you save by replacing your existing heating. This is the number on each product card.') + '</em></span>';
      } else {
        h += '<span><strong>' + (L.comp_col_total_earnings || 'Total') + ' = ' + (L.mining_revenue || 'revenue') + ' - ' + (L.electricity_cost || 'electricity') + (u.pv ? ' + PV' : '') + ' - ' + (L.pool_fees || 'pool fees') + '</strong></span>';
        h += '<span><em>' + tr('hic_total_mining_note', 'What you earn from mining after all running costs. This is the number on each product card.') + '</em></span>';
      }
      stepNum++;

      // Step: Break-even & ROI
      h += '<span>&nbsp;</span>';
      h += '<span><strong>Step ' + stepNum + ': ' + tr('hic_step_roi', 'Break-even & ROI') + '</strong></span>';
      h += '<span>' + (L.break_even || 'Break-even') + ' = price / (earnings / 12)</span>';
      h += '<span>ROI = (earnings - price) / price x 100%</span>';
      h += '<span><em>' + tr('hic_roi_note', 'Break-even: how many months until the device pays for itself. ROI: your percentage return over [years] years. Positive = profit, negative = not yet paid off.', { years: opts.timePeriodYears || 1 }) + '</em></span>';

    } else {
      // Mining Calculator tab (simpler, no step-by-step)
      if (u.minerSpecs) h += '<span>' + tr('hic_hashrate_power', 'Hashrate: [hashrate] TH/s · Power: [power] W', { hashrate: opts.minerHashrate, power: opts.minerPower }) + '</span>';
      h += '<span>' + tr('hic_operating', 'Operating: [days] days/year', { days: opts.operatingDays }) + '</span>';
      h += '<span>&nbsp;</span>';
      h += '<span><strong>' + tr('hic_formulas', 'Formulas') + '</strong></span>';
      h += '<span>BTC/' + (L.unit_day || 'day') + ' = (hashrate / network) x 144 x ' + opts.blockReward + ' BTC</span>';
      h += '<span>' + (L.mining_revenue || 'Revenue') + ' = BTC x ' + opts.btcPrice + '</span>';
      h += '<span>' + (L.electricity_cost || 'Electricity') + ' = power x ' + opts.electricityCost + ' ' + opts.currSymbol + '/kWh x ' + (L.unit_day || 'days') + '</span>';
      h += '<span>' + (L.pool_fees || 'Pool fees') + ' = ' + opts.poolFee + '% x ' + (L.mining_revenue || 'revenue') + '</span>';
      h += '<span>' + (L.estimated_profit || 'Profit') + ' = ' + (L.mining_revenue || 'revenue') + ' - ' + (L.electricity_cost || 'electricity') + ' - ' + (L.pool_fees || 'pool fees') + '</span>';
      if (u.historical) h += '<span><em>' + tr('hic_historical_note', 'Historical section uses actual averaged price and difficulty data from each period.') + '</em></span>';
    }

    return h;
  }

  // Format BTC price for display in the input (full formatted string)
  function formatPriceForInput(price) {
    var formatted = formatMoney(price);
    // Strip currency symbol — the suffix already shows it
    if (activeCurrencySymbol) {
      formatted = formatted.replace(activeCurrencySymbol, '').trim();
    }
    return formatted;
  }

  // Parse a formatted price string back to a number
  function parsePriceInput(str) {
    // Strip everything except digits, dots, and commas
    var cleaned = str.replace(/[^\d.,]/g, '');
    // If both dot and comma exist, figure out which is the decimal separator
    var hasDot = cleaned.indexOf('.') !== -1;
    var hasComma = cleaned.indexOf(',') !== -1;
    if (hasDot && hasComma) {
      // Whichever comes last is the decimal separator
      if (cleaned.lastIndexOf(',') > cleaned.lastIndexOf('.')) {
        cleaned = cleaned.replace(/\./g, '').replace(',', '.');
      } else {
        cleaned = cleaned.replace(/,/g, '');
      }
    } else if (hasComma) {
      // Could be thousands separator (1,000) or decimal (1,50)
      // If only one comma and exactly 3 digits after it, treat as thousands
      var parts = cleaned.split(',');
      if (parts.length === 2 && parts[1].length === 3) {
        cleaned = cleaned.replace(',', '');
      } else {
        cleaned = cleaned.replace(',', '.');
      }
    }
    return parseFloat(cleaned) || 0;
  }

  // No-op: price input is now full-width like other fields
  function autoSizePriceInput() {}

  // Update the live/custom badge state
  function updatePriceBadge() {
    var isLive = liveBtcPrice > 0 && Math.abs(btcPrice - liveBtcPrice) < 1;
    if (isLive) {
      elPriceBadge.classList.remove('bmc-calc__price-badge--custom');
      elPriceBadgeText.textContent = labels.price_live || 'Live';
      elPriceReset.hidden = true;
    } else {
      elPriceBadge.classList.add('bmc-calc__price-badge--custom');
      elPriceBadgeText.textContent = labels.price_custom || 'Custom';
      elPriceReset.hidden = !liveBtcPrice;
    }
  }

  // Reset all table cells to em-dash for a given tbody
  var elProjectionCards = document.getElementById('bmcProjectionCards');
  var elHistoricalCards = document.getElementById('bmcHistoricalCards');
  var miningPeriods = [
    { days: 1, label: labels.period_24h || '24 hours' },
    { days: 7, label: labels.period_7d || '7 days' },
    { days: 30, label: labels.period_30d || '30 days' },
    { days: 365, label: labels.period_365d || '365 days' }
  ];

  function renderMiningCards(container, btcPriceForCalc, networkHashrateTHForCalc, periodPrefix) {
    if (!container) return;
    var hashrate = parseFloat(elHashrate.value) || 0;
    var power = parseFloat(elPower.value) || 0;
    var electricityCost = parseFloat(elElectricity.value) || 0;
    var poolFeePercent = parseFloat(elPoolFee.value) || 0;

    if (hashrate === 0 || btcPriceForCalc === 0 || networkHashrateTHForCalc === 0) {
      return; // Keep placeholder cards visible until data loads
    }

    var btcPerDay = (hashrate / networkHashrateTHForCalc) * BLOCKS_PER_DAY * BLOCK_REWARD;
    var incomePerDay = btcPerDay * btcPriceForCalc;
    var powerCostPerDay = computePvPowerCostPerDay(power, electricityCost);
    var poolFeePerDay = incomePerDay * (poolFeePercent / 100);
    var profitPerDay = incomePerDay - powerCostPerDay - poolFeePerDay;

    var html = '';
    for (var i = 0; i < miningPeriods.length; i++) {
      var days = miningPeriods[i].days;
      var label = (periodPrefix || '') + miningPeriods[i].label;
      var revenue = incomePerDay * days;
      var btcMined = btcPerDay * days;
      var elecCost = powerCostPerDay * days;
      var poolFee = poolFeePerDay * days;
      var profit = profitPerDay * days;
      var profitClass = profit >= 0 ? 'bmc-mining-card__profit--positive' : 'bmc-mining-card__profit--negative';

      html += '<div class="bmc-mining-card">';
      html += '<div class="bmc-mining-card__header">';
      html += '<span class="bmc-mining-card__period">' + label + '</span>';
      html += '</div>';
      html += '<div class="bmc-mining-card__breakdown">';
      html += '<div class="bmc-mining-card__row"><span>' + (labels.mining_revenue || 'Mining revenue') + '</span><span>+' + formatMoney(revenue) + '</span></div>';
      html += '<div class="bmc-mining-card__row bmc-mining-card__row--sub">' + btcMined.toFixed(8) + ' BTC</div>';
      html += '<div class="bmc-mining-card__row"><span>' + (labels.electricity_cost || 'Electricity cost') + '</span><span>-' + formatMoney(elecCost) + '</span></div>';
      html += '<div class="bmc-mining-card__row"><span>' + (labels.pool_fees || 'Pool fees') + '</span><span>-' + formatMoney(poolFee) + '</span></div>';
      html += '<div class="bmc-mining-card__row bmc-mining-card__row--total ' + profitClass + '"><span>' + (labels.estimated_profit || 'Estimated profit') + '</span><span>' + formatMoney(profit) + '</span></div>';
      html += '</div>';
      html += '</div>';
    }
    container.innerHTML = html;
  }

  // Compute daily electricity cost with optional PV offset (Mining Calculator tab)
  function computePvPowerCostPerDay(power, electricityCost) {
    var dailyConsumption = (power / 1000) * 24;
    var pvOn = elPvToggle && elPvToggle.checked;

    if (pvOn) {
      var pvSize = parseFloat(elPvSize.value) || 0;
      var pvSunshine = parseFloat(elPvSunshine.value) || 0;
      var pvAllocation = parseFloat(elPvAllocation.value) || 0;
      var feedInTariff = parseFloat(elFeedInTariff.value) || 0;

      if (pvSize > 0 && pvSunshine > 0) {
        var dailyPvAvailable = pvSize * pvSunshine * (pvAllocation / 100);
        var pvCoveredKwh = Math.min(dailyPvAvailable, dailyConsumption);
        var gridKwh = dailyConsumption - pvCoveredKwh;
        return (gridKwh * electricityCost) + (pvCoveredKwh * feedInTariff);
      }
    }

    return dailyConsumption * electricityCost;
  }

  // Compute average of y-values in a time-series within the last N days.
  // The API returns daily data points (one per day at midnight UTC), so for
  // daysBack=1 the cutoff may exclude all points. Fall back to the last
  // available data point when no points fall within the window.
  function computeAverageInWindow(dataPoints, daysBack) {
    if (!dataPoints || dataPoints.length === 0) return 0;
    var now = Date.now() / 1000;
    var cutoff = now - (daysBack * 86400);
    var sum = 0;
    var count = 0;
    for (var i = 0; i < dataPoints.length; i++) {
      if (dataPoints[i].x >= cutoff) {
        sum += dataPoints[i].y;
        count++;
      }
    }
    // Fallback: use the most recent data point if nothing matched
    if (count === 0) {
      return dataPoints[dataPoints.length - 1].y;
    }
    return sum / count;
  }

  // Main calculation — projection rows
  function calculate() {
    var hashrate = parseFloat(elHashrate.value) || 0;

    renderMiningCards(elProjectionCards, btcPrice, networkHashrateTH, '');

    // Update "How it's calculated" using shared builder
    var elMiningCalcDetails = document.getElementById('bmcMiningCalcDetails');
    if (elMiningCalcDetails && networkHashrateTH > 0) {
      elMiningCalcDetails.innerHTML = buildCalcDetails({
        used: { timePeriod: false, minerSpecs: true, heating: false, forecast: false, halvings: false, hodl: false, pv: false, historical: true },
        timePeriod: '—', timePeriodYears: 1,
        btcPrice: formatMoney(btcPrice),
        hashrate: (networkHashrateTH / 1e6).toFixed(2),
        difficulty: (difficulty / 1e12).toFixed(2),
        blockReward: BLOCK_REWARD,
        minerHashrate: (parseFloat(elHashrate.value) || 0),
        minerPower: formatNumber(parseFloat(elPower.value) || 0),
        electricityCost: (parseFloat(elElectricity.value) || 0).toFixed(2),
        heatingCost: '0.00', heatDemand: '0', heatingMonths: '0', heatingDays: '0',
        btcGrowth: '0', diffGrowth: '0',
        pvSize: '0', pvSunshine: '0', feedInTariff: '0.00',
        poolFee: (parseFloat(elPoolFee.value) || 0),
        operatingDays: '365',
        currSymbol: activeCurrencySymbol,
        hodlPrice: ''
      });
    }

    // Also update historical if ready
    calculateHistorical();
  }

  // Historical calculation — uses per-row averaged difficulty/price
  function calculateHistorical() {
    if (!historicalDataReady || !elHistoricalCards) return;

    var hashrate = parseFloat(elHashrate.value) || 0;
    if (hashrate === 0) {
      elHistoricalCards.innerHTML = '';
      return;
    }

    var power = parseFloat(elPower.value) || 0;
    var electricityCost = parseFloat(elElectricity.value) || 0;
    var poolFeePercent = parseFloat(elPoolFee.value) || 0;
    var histLabels = [
      labels.period_past_24h || 'Last 24 hours',
      labels.period_past_7d || 'Last 7 days',
      labels.period_past_30d || 'Last 30 days',
      labels.period_past_365d || 'Last 365 days'
    ];

    var html = '';
    for (var i = 0; i < miningPeriods.length; i++) {
      var days = miningPeriods[i].days;
      var label = histLabels[i];

      var avgDifficulty = computeAverageInWindow(historicalDifficultyData, days);
      var avgPriceUSD = computeAverageInWindow(historicalPriceDataUSD, days);

      if (avgDifficulty === 0 || avgPriceUSD === 0) continue;

      var avgPrice = convertUsdToActiveCurrency(avgPriceUSD);
      var avgNetworkHashrateTH = avgDifficulty * Math.pow(2, 32) / 600 / 1e12;

      var btcPerDay = (hashrate / avgNetworkHashrateTH) * BLOCKS_PER_DAY * BLOCK_REWARD;
      var incomePerDay = btcPerDay * avgPrice;
      var powerCostPerDay = computePvPowerCostPerDay(power, electricityCost);
      var poolFeePerDay = incomePerDay * (poolFeePercent / 100);
      var profitPerDay = incomePerDay - powerCostPerDay - poolFeePerDay;

      var revenue = incomePerDay * days;
      var btcMined = btcPerDay * days;
      var elecCost = powerCostPerDay * days;
      var poolFee = poolFeePerDay * days;
      var profit = profitPerDay * days;
      var profitClass = profit >= 0 ? 'bmc-mining-card__profit--positive' : 'bmc-mining-card__profit--negative';
      var avgDiffFormatted = (avgDifficulty / 1e12).toFixed(2);

      html += '<div class="bmc-mining-card">';
      html += '<div class="bmc-mining-card__header">';
      html += '<span class="bmc-mining-card__period">' + label + '</span>';
      html += '</div>';
      html += '<div class="bmc-mining-card__avg-hint">' + (labels.historical_avg || 'Avg. price: {{ price }} · Difficulty: {{ diff }} T').replace('{{ price }}', formatMoney(avgPrice)).replace('{{ diff }}', avgDiffFormatted) + '</div>';
      html += '<div class="bmc-mining-card__breakdown">';
      html += '<div class="bmc-mining-card__row"><span>' + (labels.mining_revenue || 'Mining revenue') + '</span><span>+' + formatMoney(revenue) + '</span></div>';
      html += '<div class="bmc-mining-card__row bmc-mining-card__row--sub">' + btcMined.toFixed(8) + ' BTC</div>';
      html += '<div class="bmc-mining-card__row"><span>' + (labels.electricity_cost || 'Electricity cost') + '</span><span>-' + formatMoney(elecCost) + '</span></div>';
      html += '<div class="bmc-mining-card__row"><span>' + (labels.pool_fees || 'Pool fees') + '</span><span>-' + formatMoney(poolFee) + '</span></div>';
      html += '<div class="bmc-mining-card__row bmc-mining-card__row--total ' + profitClass + '"><span>' + (labels.estimated_profit || 'Estimated profit') + '</span><span>' + formatMoney(profit) + '</span></div>';
      html += '</div>';
      html += '</div>';
    }
    elHistoricalCards.innerHTML = html;
  }

  // Fetch historical difficulty + price data from blockchain.info Charts API
  function fetchHistoricalData() {
    var diffPromise = fetch('https://api.blockchain.info/charts/difficulty?timespan=1year&format=json&cors=true')
      .then(function(res) { return res.json(); })
      .then(function(data) {
        historicalDifficultyData = data.values || [];
      });

    var pricePromise = fetch('https://api.blockchain.info/charts/market-price?timespan=1year&format=json&cors=true')
      .then(function(res) { return res.json(); })
      .then(function(data) {
        historicalPriceDataUSD = data.values || [];
      });

    Promise.all([diffPromise, pricePromise])
      .then(function() {
        historicalDataReady = true;
        calculateHistorical();
      })
      .catch(function() {
        if (elHistoricalCards) {
          elHistoricalCards.innerHTML = '<div class="bmc-mining-card"><div class="bmc-mining-card__header"><span class="bmc-mining-card__period">Historical data unavailable</span></div></div>';
        }
      });
  }

  // Tooltip toggle
  function initTooltips() {
    // Delegated click handler — works for both static and dynamically rendered tooltips
    document.addEventListener('click', function(e) {
      var btn = e.target.closest('.bmc-calc__info-btn');

      // Close all tooltips first
      var allTooltips = document.querySelectorAll('.bmc-calc__tooltip');
      var allBtns = document.querySelectorAll('.bmc-calc__info-btn');
      for (var j = 0; j < allTooltips.length; j++) {
        allTooltips[j].hidden = true;
      }
      for (var j = 0; j < allBtns.length; j++) {
        allBtns[j].removeAttribute('aria-expanded');
      }

      // Toggle the clicked tooltip
      if (btn) {
        e.stopPropagation();
        var tooltipId = btn.getAttribute('data-tooltip');
        var tooltip = document.getElementById(tooltipId);
        if (tooltip) {
          tooltip.hidden = false;
          btn.setAttribute('aria-expanded', 'true');
        }
      }
    });
  }

  // Tab switching
  // Tab ID to hash mapping
  var tabHashMap = {
    'bmcTabMining': 'mining',
    'bmcTabProfitability': 'profitability'
  };

  function activateTabByHash(hash) {
    for (var panelId in tabHashMap) {
      if (tabHashMap[panelId] === hash) {
        var btn = document.querySelector('.bmc-calc__tab[aria-controls="' + panelId + '"]');
        if (btn && !btn.classList.contains('bmc-calc__tab--active')) btn.click();
        var section = document.getElementById('mining');
        if (section) section.scrollIntoView({ behavior: 'smooth' });
        return true;
      }
    }
    return false;
  }

  function initTabs() {
    var tabs = document.querySelectorAll('.bmc-calc__tab[aria-controls]');
    for (var i = 0; i < tabs.length; i++) {
      (function(tab) {
        tab.addEventListener('click', function() {
          var panelId = tab.getAttribute('aria-controls');
          var tabGroup = tab.parentElement;
          var allTabs = tabGroup.querySelectorAll('.bmc-calc__tab');
          // Hide all panels in this tab group
          for (var j = 0; j < allTabs.length; j++) {
            allTabs[j].classList.remove('bmc-calc__tab--active');
            allTabs[j].setAttribute('aria-selected', 'false');
            var pId = allTabs[j].getAttribute('aria-controls');
            var p = document.getElementById(pId);
            if (p) { p.hidden = true; p.classList.remove('bmc-calc__tab-content--active'); }
          }
          // Show clicked panel
          tab.classList.add('bmc-calc__tab--active');
          tab.setAttribute('aria-selected', 'true');
          var panel = document.getElementById(panelId);
          if (panel) { panel.hidden = false; panel.classList.add('bmc-calc__tab-content--active'); }
          // Update URL hash
          var hash = tabHashMap[panelId];
          if (hash) history.replaceState(null, '', '#' + hash);
        });
      })(tabs[i]);
    }

    // Deep link: activate tab from URL hash on load
    var hash = window.location.hash.replace('#', '');
    if (hash) activateTabByHash(hash);

    // Handle back/forward navigation
    window.addEventListener('hashchange', function() {
      var h = window.location.hash.replace('#', '');
      if (h) activateTabByHash(h);
    });

    // Intercept in-page hash links for smooth scroll + tab activation
    document.addEventListener('click', function(e) {
      var link = e.target.closest('a[href^="#mining"], a[href^="#profitability"]');
      if (!link) return;
      var h = link.getAttribute('href').replace('#', '');
      e.preventDefault();
      history.replaceState(null, '', '#' + h);
      activateTabByHash(h);
    });

    // "View Bitcoin Heaters" button switches to profitability tab
    var viewHeatersBtn = document.getElementById('bmcViewHeatersBtn');
    if (viewHeatersBtn) {
      viewHeatersBtn.addEventListener('click', function() {
        var profTab = document.getElementById('bmcTabBtnProfitability');
        if (profTab) profTab.click();
      });
    }
  }

  // ========== Profitability Tab ==========

  // Device configurations (prices in EUR)
  // Convert EUR amount to active currency using BTC cross rate
  function convertEurToActive(eurAmount) {
    if (activeCurrency === 'EUR') return eurAmount;
    if (!tickerData) return eurAmount;
    var eurBtc = tickerData['EUR'] && tickerData['EUR'].last;
    var activeBtc = tickerData[activeCurrency] && tickerData[activeCurrency].last;
    if (eurBtc && activeBtc) return eurAmount * (activeBtc / eurBtc);
    return eurAmount;
  }

  // ========== Product Comparison Tab (Profitability Calculator) ==========

  // Build compProducts from Liquid-generated product data (collections: bitcoin-heater + bitcoin-miner)
  // Heaters: one entry per product (first available variant)
  // Miners: one entry per variant (each variant is its own row)
  var productData = window.bmc2ProductData || {};
  var compProducts = [];
  var productKeys = Object.keys(productData);
  for (var di = 0; di < productKeys.length; di++) {
    var key = productKeys[di];
    if (key === '_end') continue;
    var pd = productData[key];
    if (!pd || !pd.variants || !pd.variants.length) continue;

    if (pd.isHeater) {
      // Heaters: use first available variant only
      var v = pd.variants[0];
      compProducts.push({
        key: key,
        productKey: key,
        name: pd.title,
        power: (v.power_w && v.power_w > 0) ? v.power_w : 0,
        isHeater: true,
        // Quantity bundles are not configured for every heater; absent means yes,
        // so products predating the flag keep their existing card.
        hasBundle: pd.bundle !== false,
        group: pd.group || 'Heaters',
        hashrate: (v.hashrate_th && v.hashrate_th > 0) ? v.hashrate_th : 0,
        // Spec-sheet J/TH where we have it. Only Ofen 3 differs from power/hashrate.
        efficiencyJth: (v.efficiency_jth && v.efficiency_jth > 0) ? v.efficiency_jth : 0,
        variantId: v.id,
        price: v.price || 0,
        compareAtPrice: v.compare_at_price || 0,
        available: v.available,
        image: v.image || '',
        url: pd.url || '#',
        badge: pd.badge || '',
        onSale: v.compare_at_price > 0 && v.compare_at_price > v.price
      });
    } else {
      // Miners: one entry per variant
      var hasMultipleVariants = pd.variants.length > 1;
      for (var vi = 0; vi < pd.variants.length; vi++) {
        var v = pd.variants[vi];
        var displayName = pd.title;
        if (hasMultipleVariants && v.title && v.title !== 'Default Title') {
          displayName += ' (' + v.title + ')';
        }
        compProducts.push({
          key: key + '::' + v.id,
          productKey: key,
          name: displayName,
          power: (v.power_w && v.power_w > 0) ? v.power_w : 0,
          isHeater: false,
          isSoloMiner: pd.isSoloMiner || false,
          group: pd.group || 'Other',
          hashrate: (v.hashrate_th && v.hashrate_th > 0) ? v.hashrate_th : 0,
          efficiencyJth: (v.efficiency_jth && v.efficiency_jth > 0) ? v.efficiency_jth : 0,
          variantId: v.id,
          price: v.price || 0,
          compareAtPrice: v.compare_at_price || 0,
          available: v.available,
          image: v.image || '',
          url: pd.url || '#',
          badge: pd.badge || '',
          onSale: v.compare_at_price > 0 && v.compare_at_price > v.price
        });
      }
    }
  }

  // Sort miners by group (manufacturer) so same-group products are contiguous
  // Heaters first, then miners (sorted by ROI after first calculation)
  compProducts.sort(function(a, b) {
    if (a.isHeater !== b.isHeater) return a.isHeater ? -1 : 1;
    return 0;
  });


  // Flags for one-time earnings sort


  // Quantity per product (keyed by compound key, defaults to 1)
  var compQuantities = {};

  // Comparison tab DOM refs
  var elCompElectricity = document.getElementById('bmcCompElectricity');
  var elCompElectricitySuffix = document.getElementById('bmcCompElectricitySuffix');
  var elCompPriceSuffix = document.getElementById('bmcCompPriceSuffix');
  var elCompHeatingCost = document.getElementById('bmcCompHeatingCost');
  var elCompHeatingCostSuffix = document.getElementById('bmcCompHeatingCostSuffix');
  var elCompPoolFee = document.getElementById('bmcCompPoolFee');
  var elCompCurrency = document.getElementById('bmcCompCurrency');
  var elMinerCards = document.getElementById('bmcMinerCards');
  var elCompDisclaimer = document.getElementById('bmcCompDisclaimer');
  var elCompBtcPrice = document.getElementById('bmcCompBtcPrice');
  var elCompPriceBadge = document.getElementById('bmcCompPriceBadge');
  var elCompPriceBadgeText = document.getElementById('bmcCompPriceBadgeText');

  // Toggle DOM refs
  var elCompHeatingInputs = document.getElementById('bmcCompHeatingInputs');
  var elCompHeatDemand = document.getElementById('bmcCompHeatDemand');
  var elCompHeatingMonths = document.getElementById('bmcCompHeatingMonths');
  var elCompHeatingMonthsHint = document.getElementById('bmcCompHeatingMonthsHint');
  var elHeaterSeasonHint = document.getElementById('bmcHeaterSeasonHint');
  var elCompHalvingToggle = document.getElementById('bmcCompHalvingToggle');
  var elCompHalvingHint = document.getElementById('bmcCompHalvingHint');
  var elCompHodlToggle = document.getElementById('bmcCompHodlToggle');
  var elCompHodlRow = document.getElementById('bmcCompHodlRow');
  var elCompHodlHint = document.getElementById('bmcCompHodlHint');
  var elHeaterCards = document.getElementById('bmcHeaterCards');
  var elCompPvToggle = document.getElementById('bmcCompPvToggle');
  var elCompPvGroup = document.getElementById('bmcCompPvGroup');
  var elCompPvSize = document.getElementById('bmcCompPvSize');
  var elCompPvSunshine = document.getElementById('bmcCompPvSunshine');
  var elCompPvAllocation = document.getElementById('bmcCompPvAllocation');
  var elCompFeedInTariff = document.getElementById('bmcCompFeedInTariff');
  var elCompFeedInSuffix = document.getElementById('bmcCompFeedInSuffix');

  // Price mode DOM refs
  var elCompPriceMode = document.getElementById('bmcCompPriceMode');
  var elCompPriceCurrent = document.getElementById('bmcCompPriceCurrent');
  var elCompPriceAvg = document.getElementById('bmcCompPriceAvg');
  var elCompPriceCustom = document.getElementById('bmcCompPriceCustom');
  var elCompCustomPrice = document.getElementById('bmcCompCustomPrice');
  var elCompCustomPriceSuffix = document.getElementById('bmcCompCustomPriceSuffix');
  var elCompAvgDisplay = document.getElementById('bmcCompAvgDisplay');
  var elCompAvgSuffix = document.getElementById('bmcCompAvgSuffix');
  var elCompForecastToggle = document.getElementById('bmcCompForecastToggle');
  var elCompForecastGroup = document.getElementById('bmcCompForecastGroup');
  var elCompForecastSummary = document.getElementById('bmcCompForecastSummary');
  var elCompForecastEditBtn = document.getElementById('bmcCompForecastEditBtn');
  var elCompForecastInputs = document.getElementById('bmcCompForecastInputs');
  var elCompBtcGrowth = document.getElementById('bmcCompBtcGrowth');
  var elCompDiffGrowth = document.getElementById('bmcCompDiffGrowth');

  // Timeframe DOM refs
  var elCompTimeframe = document.getElementById('bmcCompTimeframe');
  var elCompCustomTimeframe = document.getElementById('bmcCompCustomTimeframe');
  var elCompCustomYears = document.getElementById('bmcCompCustomYears');
  var elCompTimeframeHint = document.getElementById('bmcCompTimeframeHint');

  // Miner heating toggle DOM refs
  var elCompMinerHeatingToggle = document.getElementById('bmcCompMinerHeatingToggle');
  var elCompMinerHeatingCheckbox = document.getElementById('bmcCompMinerHeatingCheckbox');
  var elCompMinerHeatingWarning = document.getElementById('bmcCompMinerHeatingWarning');
  var elCompMinerRuntimeField = document.getElementById('bmcCompMinerRuntimeField');
  var elCompMinerRuntime = document.getElementById('bmcCompMinerRuntime');

  // View toggle DOM refs
  var elCompProductType = document.getElementById('bmcCompProductType');
  var elCompViewHeaters = document.getElementById('bmcCompViewHeaters');
  var elCompViewMiners = document.getElementById('bmcCompViewMiners');
  var elCompDifficultyHint = document.getElementById('bmcCompDifficultyHint');



  // Update miner heating toggle, warning, runtime, and heating input visibility
  function updateMinerHeatingVisibility() {
    var isMiner = compActiveView === 'miners';
    var heatingOn = elCompMinerHeatingCheckbox && elCompMinerHeatingCheckbox.checked;

    if (elCompMinerHeatingToggle) elCompMinerHeatingToggle.hidden = !isMiner;
    if (elCompMinerHeatingWarning) elCompMinerHeatingWarning.hidden = !(isMiner && heatingOn);
    if (elCompMinerRuntimeField) elCompMinerRuntimeField.hidden = !(isMiner && heatingOn);

    // Heating inputs: shown for heaters always, or for miners when heating toggle is ON
    if (elCompHeatingInputs) {
      elCompHeatingInputs.hidden = isMiner ? !heatingOn : false;
      // Green left border when shown as part of miner heating toggle
      if (isMiner && heatingOn) {
        elCompHeatingInputs.classList.add('bmc-calc__heating-inputs--bordered');
      } else {
        elCompHeatingInputs.classList.remove('bmc-calc__heating-inputs--bordered');
      }
    }
  }

  // Toggle group visibility

  function addToCart(variantId, quantity) {
    var cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
    var sections = cart && typeof cart.getSectionsToRender === 'function'
      ? cart.getSectionsToRender().map(function(s) { return s.id; }).join(',')
      : '';

    var body = JSON.stringify({
      items: [{ id: variantId, quantity: quantity }],
      sections: sections,
      sections_url: window.location.pathname
    });

    return fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: body
    })
    .then(function(res) { return res.json(); })
    .then(function(response) {
      if (response.status) throw new Error(response.description);
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'bmc-calculator', productVariantId: variantId });
      }
      if (cart && typeof cart.renderContents === 'function' && response.sections) {
        cart.renderContents(response);
      } else if (cart && typeof cart.open === 'function') {
        cart.open();
      } else {
        window.location.href = '/cart';
      }
    });
  }

  function toggleGroup(toggle, group) {
    if (toggle.checked) {
      group.classList.add('bmc-calc__toggle-group--open');
    } else {
      group.classList.remove('bmc-calc__toggle-group--open');
    }
  }

  // Get the effective BTC price for the profitability tab based on the current mode
  function getCompBtcPrice() {
    if (compPriceMode === 'avg4y' && avgDataReady) {
      return convertUsdToActiveCurrency(fourYearAvgUSD);
    }
    if (compPriceMode === 'avg1y' && avgDataReady) {
      return convertUsdToActiveCurrency(oneYearAvgUSD);
    }
    if (compPriceMode === 'custom' && elCompCustomPrice) {
      var parsed = parsePriceInput(elCompCustomPrice.value);
      return parsed > 0 ? parsed : btcPrice;
    }
    // 'current' falls through to btcPrice (live)
    return btcPrice;
  }

  // Switch price mode UI
  function switchPriceMode(mode) {
    compPriceMode = mode;
    if (elCompPriceMode) elCompPriceMode.value = mode;

    // Show/hide mode groups
    if (elCompPriceCurrent) elCompPriceCurrent.hidden = (mode !== 'current');
    if (elCompPriceAvg) elCompPriceAvg.hidden = (mode !== 'avg4y' && mode !== 'avg1y');
    if (elCompPriceCustom) elCompPriceCustom.hidden = (mode !== 'custom');
    // HODL toggle only visible when forecast is enabled
    if (elCompHodlRow) elCompHodlRow.hidden = !forecastEnabled;
    // Halving price increase only visible when forecast OFF + halvings ON
    var halvingOn = elCompHalvingToggle && elCompHalvingToggle.checked;
    if (elCompHodlHint) elCompHodlHint.hidden = !forecastEnabled;
    // Live badge only visible for current mode
    if (elCompPriceBadge) elCompPriceBadge.hidden = (mode !== 'current');

    // Update average display
    if ((mode === 'avg4y' || mode === 'avg1y') && elCompAvgDisplay) {
      var avgPrice = getCompBtcPrice();
      elCompAvgDisplay.value = avgPrice > 0 ? formatPriceForInput(avgPrice) : '...';
      if (elCompAvgSuffix) elCompAvgSuffix.textContent = activeCurrencySymbol;
    }

    // Seed custom price with current live price
    if (mode === 'custom' && elCompCustomPrice) {
      if (elCompCustomPrice.value === '...') {
        elCompCustomPrice.value = btcPrice > 0 ? formatPriceForInput(btcPrice) : '...';
      }
      if (elCompCustomPriceSuffix) elCompCustomPriceSuffix.textContent = activeCurrencySymbol;
    }

    updateDifficultyHint();
    updateHalvingHint();
    updateHodlHint();
    calculateComparison();
  }

  // Get the raw difficulty for the current price mode
  function getCompDifficulty() {
    if (compPriceMode === 'avg4y' && avgDataReady && fourYearAvgDifficulty > 0) {
      return fourYearAvgDifficulty;
    }
    if (compPriceMode === 'avg1y' && avgDataReady && oneYearAvgDifficulty > 0) {
      return oneYearAvgDifficulty;
    }
    return difficulty;
  }

  // Update the difficulty hint text below the price display
  function formatShortDate(ts) {
    var d = new Date(ts * 1000);
    var locale = document.documentElement.lang || 'en';
    return d.toLocaleDateString(locale, { year: 'numeric', month: 'short', day: 'numeric' });
  }

  function updateDifficultyHint() {
    if (!elCompDifficultyHint) return;
    var nh = getCompNetworkHashrateTH();
    var diff = getCompDifficulty();
    if (nh <= 0 || diff <= 0) {
      elCompDifficultyHint.textContent = '';
      return;
    }
    var nhFormatted = (nh / 1e6).toFixed(2);
    var diffFormatted = (diff / 1e12).toFixed(2);

    var line1 = 'Difficulty: ' + diffFormatted + ' T · Hashrate: ' + nhFormatted + ' EH/s';

    var line2 = '';
    var todayStr = formatShortDate(Date.now() / 1000);
    var tfLabel = (labels.calc_timeframe || 'Timeframe') + ': ';
    if (compPriceMode === 'avg4y' && fourYearPriceDataUSD && fourYearPriceDataUSD.length > 0) {
      var fromStr = formatShortDate(fourYearPriceDataUSD[0].x);
      line2 = tfLabel + fromStr + ' – ' + todayStr;
    } else if (compPriceMode === 'avg1y') {
      var oneYearAgo = (Date.now() / 1000) - (365 * 86400);
      line2 = tfLabel + formatShortDate(oneYearAgo) + ' – ' + todayStr;
    } else if (compPriceMode === 'current') {
      line2 = tfLabel + todayStr;
    }

    elCompDifficultyHint.innerHTML = (line2 ? line2 + '<br>' : '') + line1;
  }

  function updateTimeframeHint() {
    if (!elCompTimeframeHint) return;
    var now = Date.now() / 1000;
    var end = now + (compTimeframeYears * 365 * 86400);
    elCompTimeframeHint.textContent = formatShortDate(now) + ' – ' + formatShortDate(end);
  }

  function updateHeatingMonthsHint() {
    if (!elCompHeatingMonthsHint) return;
    var demand = elCompHeatDemand ? (parseFloat(elCompHeatDemand.value) || 0) : 0;
    var months = elCompHeatingMonths ? (parseInt(elCompHeatingMonths.value, 10) || 7) : 7;
    if (demand <= 0 || months <= 0) {
      elCompHeatingMonthsHint.textContent = '';
      return;
    }
    var perMonth = Math.round(demand / months);
    elCompHeatingMonthsHint.textContent = '≈ ' + formatNumber(perMonth) + ' kWh/' + (labels.unit_month || 'mo') + ' ' + (labels.heating_during_season || 'during season');
  }

  function setDefaultHeaterQuantities() {
    var demand = elCompHeatDemand ? (parseFloat(elCompHeatDemand.value) || 0) : 0;
    var months = elCompHeatingMonths ? (parseInt(elCompHeatingMonths.value, 10) || 7) : 7;
    if (demand <= 0) return;
    var operatingDays = (months / 12) * 365;
    for (var i = 0; i < compProducts.length; i++) {
      var p = compProducts[i];
      if (!p.isHeater) continue;
      var dailyHeat = (p.power / 1000) * 24;
      var heatPerUnit = dailyHeat * operatingDays;
      var unitsNeeded = Math.max(1, Math.floor(demand / heatPerUnit));
      compQuantities[p.key] = unitsNeeded;
    }
  }

  function setDefaultMinerHeatingQuantities() {
    var demand = elCompHeatDemand ? (parseFloat(elCompHeatDemand.value) || 0) : 0;
    var months = elCompHeatingMonths ? (parseInt(elCompHeatingMonths.value, 10) || 7) : 7;
    var minerRuntime = (elCompMinerRuntime && elCompMinerRuntime.value) || 'year-round';
    if (demand <= 0) return;
    var heatingDays = (months / 12) * 365;
    for (var i = 0; i < compProducts.length; i++) {
      var p = compProducts[i];
      if (p.isHeater) continue;
      if (!p.power || p.power <= 0) continue;
      // Skip solo miners — heating doesn't apply
      if (p.isSoloMiner) continue;
      var dailyHeat = (p.power / 1000) * 24;
      var heatPerUnit = dailyHeat * heatingDays;
      var unitsNeeded = Math.max(1, Math.floor(demand / heatPerUnit));
      compQuantities[p.key] = unitsNeeded;
    }
  }

  function resetMinerQuantities() {
    for (var i = 0; i < compProducts.length; i++) {
      var p = compProducts[i];
      if (p.isHeater) continue;
      compQuantities[p.key] = 1;
    }
  }

  function updateHeaterSeasonHint() {
    if (!elHeaterSeasonHint) return;
    var months = elCompHeatingMonths ? (parseInt(elCompHeatingMonths.value, 10) || 7) : 7;
    elHeaterSeasonHint.textContent = (labels.season_hint || 'Calculations based on {{ months }}-month heating season').replace('{{ months }}', months);
  }

  // Get block reward for a given calendar year (accounts for halvings)
  function getBlockRewardForYear(calendarYear) {
    var reward = BLOCK_REWARD; // 3.125 (current)
    for (var i = 0; i < HALVING_SCHEDULE.length; i++) {
      if (calendarYear >= HALVING_SCHEDULE[i].year) {
        reward = HALVING_SCHEDULE[i].reward;
      }
    }
    return reward;
  }

  // Get halvings that occur within the calculation timeframe
  function getHalvingsInRange() {
    var now = new Date();
    var startYear = now.getFullYear();
    var endYear = startYear + compTimeframeYears;
    var halvings = [];
    for (var i = 0; i < HALVING_SCHEDULE.length; i++) {
      var h = HALVING_SCHEDULE[i];
      if (h.year > startYear && h.year <= endYear) {
        var prevReward = i === 0 ? BLOCK_REWARD : HALVING_SCHEDULE[i - 1].reward;
        halvings.push({ year: h.year, from: prevReward, to: h.reward });
      }
    }
    return halvings;
  }

  function updateHalvingHint() {
    if (!elCompHalvingHint) return;
    var halvingOn = elCompHalvingToggle && elCompHalvingToggle.checked;
    if (!halvingOn) {
      elCompHalvingHint.textContent = '';
      return;
    }
    var halvings = getHalvingsInRange();
    if (halvings.length === 0) {
      elCompHalvingHint.textContent = labels.no_halvings || 'No halvings within this timeframe';
      return;
    }
    var parts = [];
    for (var i = 0; i < halvings.length; i++) {
      var h = halvings[i];
      var halvingDate = new Date(h.year, 3, 1).toLocaleDateString(document.documentElement.lang || 'en', { year: 'numeric', month: 'short' });
      parts.push((labels.halving_hint || '~{{ date }}: {{ from }} → {{ to }} BTC/block').replace('{{ date }}', halvingDate).replace('{{ from }}', h.from).replace('{{ to }}', h.to));
    }
    elCompHalvingHint.innerHTML = parts.join('<br>');
  }

  function updateHodlHint() {
    if (!elCompHodlHint) return;
    var hodlOn = elCompHodlToggle && elCompHodlToggle.checked;
    if (!hodlOn || !forecastEnabled) {
      elCompHodlHint.textContent = '';
      return;
    }
    var btcGrowthRate = (parseFloat(elCompBtcGrowth.value) || 0) / 100;
    var finalPrice = getCompBtcPrice() * Math.pow(1 + btcGrowthRate, compTimeframeYears);
    elCompHodlHint.textContent = (labels.projected_btc_price || 'Projected BTC price in year {{ years }}').replace('{{ years }}', compTimeframeYears) + ': ' + formatMoney(finalPrice);
  }

  // Compute averages from 4-year price + difficulty data
  function computeAverages() {
    // Price averages
    if (fourYearPriceDataUSD && fourYearPriceDataUSD.length > 0) {
      var sum4p = 0;
      for (var i = 0; i < fourYearPriceDataUSD.length; i++) {
        sum4p += fourYearPriceDataUSD[i].y;
      }
      fourYearAvgUSD = sum4p / fourYearPriceDataUSD.length;

      var now = Date.now() / 1000;
      var cutoff1y = now - (365 * 86400);
      var sum1p = 0, count1p = 0;
      for (var i = 0; i < fourYearPriceDataUSD.length; i++) {
        if (fourYearPriceDataUSD[i].x >= cutoff1y) {
          sum1p += fourYearPriceDataUSD[i].y;
          count1p++;
        }
      }
      oneYearAvgUSD = count1p > 0 ? sum1p / count1p : fourYearAvgUSD;
    }

    // Difficulty averages
    if (fourYearDifficultyData && fourYearDifficultyData.length > 0) {
      var sum4d = 0;
      for (var i = 0; i < fourYearDifficultyData.length; i++) {
        sum4d += fourYearDifficultyData[i].y;
      }
      fourYearAvgDifficulty = sum4d / fourYearDifficultyData.length;

      var now2 = Date.now() / 1000;
      var cutoff1yd = now2 - (365 * 86400);
      var sum1d = 0, count1d = 0;
      for (var i = 0; i < fourYearDifficultyData.length; i++) {
        if (fourYearDifficultyData[i].x >= cutoff1yd) {
          sum1d += fourYearDifficultyData[i].y;
          count1d++;
        }
      }
      oneYearAvgDifficulty = count1d > 0 ? sum1d / count1d : fourYearAvgDifficulty;
    }

    avgDataReady = (fourYearPriceDataUSD && fourYearPriceDataUSD.length > 0 &&
                    fourYearDifficultyData && fourYearDifficultyData.length > 0);
  }

  // Fetch 4-year price + difficulty data for averages
  function fetchFourYearData() {
    var pricePromise = fetch('https://api.blockchain.info/charts/market-price?timespan=4years&format=json&cors=true')
      .then(function(res) { return res.json(); })
      .then(function(data) {
        fourYearPriceDataUSD = data.values || [];
      });

    var diffPromise = fetch('https://api.blockchain.info/charts/difficulty?timespan=4years&format=json&cors=true')
      .then(function(res) { return res.json(); })
      .then(function(data) {
        fourYearDifficultyData = data.values || [];
      });

    return Promise.all([pricePromise, diffPromise])
      .then(function() {
        computeAverages();
        // If user is on an average mode, refresh the display
        if (compPriceMode === 'avg4y' || compPriceMode === 'avg1y') {
          switchPriceMode(compPriceMode);
        }
      })
      .catch(function() {
        // Averages unavailable — modes will show "..."
      });
  }

  // Get the network hashrate for the profitability tab based on current mode
  function getCompNetworkHashrateTH() {
    if (compPriceMode === 'avg4y' && avgDataReady && fourYearAvgDifficulty > 0) {
      return fourYearAvgDifficulty * Math.pow(2, 32) / 600 / 1e12;
    }
    if (compPriceMode === 'avg1y' && avgDataReady && oneYearAvgDifficulty > 0) {
      return oneYearAvgDifficulty * Math.pow(2, 32) / 600 / 1e12;
    }
    return networkHashrateTH;
  }

  function calculateHeaters() {
    if (!elHeaterCards) return;

    var electricityCost = parseFloat(elCompElectricity.value) || 0;
    var heatingCost = parseFloat(elCompHeatingCost.value) || 0;
    var poolFeePercent = parseFloat(elCompPoolFee.value) || 0;
    var compBtcPrice = getCompBtcPrice();
    var compNetworkTH = getCompNetworkHashrateTH();
    var hasMiningData = compBtcPrice > 0 && compNetworkTH > 0;

    var pvOn = elCompPvToggle && elCompPvToggle.checked;

    // PV parameters
    var pvSize = pvOn ? (parseFloat(elCompPvSize.value) || 0) : 0;
    var pvSunshine = pvOn ? (parseFloat(elCompPvSunshine.value) || 0) : 0;
    var pvAllocation = pvOn ? (parseFloat(elCompPvAllocation.value) || 0) : 0;
    var feedInTariff = pvOn ? (parseFloat(elCompFeedInTariff.value) || 0) : 0;

    // Heating parameters (always read for heaters, no toggle check)
    var heatDemand = parseFloat(elCompHeatDemand.value) || 0;
    var heatingMonths = elCompHeatingMonths ? (parseInt(elCompHeatingMonths.value, 10) || 7) : 7;

    // --- Pass 1: Calculate heater rows ---
    var rows = [];
    for (var i = 0; i < compProducts.length; i++) {
      var p = compProducts[i];
      if (!p.isHeater) continue;

      var qty = compQuantities[p.key] || 1;

      var unitPrice = p.price > 0 ? convertEurToActive(p.price) : 0;
      var compareAtPrice = p.compareAtPrice > 0 ? convertEurToActive(p.compareAtPrice) : 0;
      var totalPrice = unitPrice * qty;
      var missingHashrate = !p.hashrate || p.hashrate <= 0;
      var missingPower = !p.power || p.power <= 0;
      var missingData = missingHashrate || missingPower;
      var efficiency = missingData ? '—' : (p.efficiencyJth > 0 ? p.efficiencyJth : p.power / p.hashrate).toFixed(1);

      var totalHashrate = p.hashrate * qty;
      var totalPower = p.power * qty;

      var heatingSavings = 0;
      var miningEarnings = 0;
      var totalEarnings = 0;
      var breakeven = -1;
      var roi = 0;
      var heatCoveragePct = 0;
      var usefulHeatKwh = 0;
      var wastedHeatKwh = 0;
      var pvCoveragePct = 0;
      var pvCoveredKwh = 0;
      var totalRevenue = 0;
      var totalElectricityCost = 0;
      var totalPoolFees = 0;
      var totalBtcMined = 0;
      var totalPvSavings = 0;
      var effectiveRateVal = 0;
      var energyPerYear = 0;

      // Heaters run during heating season only
      var operatingDaysPerYear = (heatingMonths / 12) * 365;

      if (hasMiningData) {
        var dailyConsumption = (totalPower / 1000) * 24;
        var halvingOn = elCompHalvingToggle && elCompHalvingToggle.checked;
        var hodlOn = elCompHodlToggle && elCompHodlToggle.checked && forecastEnabled;
        var isForecast = forecastEnabled;
        var btcGrowthRate = isForecast ? (parseFloat(elCompBtcGrowth.value) || 0) / 100 : 0;
        var diffGrowthRate = isForecast ? (parseFloat(elCompDiffGrowth.value) || 0) / 100 : 0;
        var startYear = new Date().getFullYear();

        // Heat coverage
        var heatOutputDuringHeating = dailyConsumption * operatingDaysPerYear;
        if (heatDemand > 0) {
          heatCoveragePct = Math.min(100, (heatOutputDuringHeating / heatDemand) * 100);
        }
        usefulHeatKwh = heatDemand > 0 ? Math.min(heatDemand, heatOutputDuringHeating) : 0;
        wastedHeatKwh = Math.max(0, heatOutputDuringHeating - usefulHeatKwh);

        // PV offset
        var gridKwh = dailyConsumption;
        if (pvOn && pvSize > 0 && pvSunshine > 0) {
          var dailyPvAvailable = pvSize * pvSunshine * (pvAllocation / 100);
          pvCoveredKwh = Math.min(dailyPvAvailable, dailyConsumption);
          gridKwh = dailyConsumption - pvCoveredKwh;
          pvCoveragePct = (pvCoveredKwh / dailyConsumption) * 100;
        }

        // Electricity at REAL rate
        var electricityPerDayFull = dailyConsumption * electricityCost;
        var electricityPerDay = electricityPerDayFull;
        var pvSavingsPerDay = 0;
        if (pvOn && pvCoveredKwh > 0) {
          electricityPerDay = (gridKwh * electricityCost) + (pvCoveredKwh * feedInTariff);
          pvSavingsPerDay = electricityPerDayFull - electricityPerDay;
        }

        effectiveRateVal = Math.max(0, electricityCost - heatingCost); // display only
        energyPerYear = dailyConsumption * operatingDaysPerYear;

        // Heating savings: flat over timeframe
        heatingSavings = heatingCost * usefulHeatKwh * compTimeframeYears;

        // Year-by-year mining calculation (supports halving + forecast + HODL)
        var totalBtcAccumulated = 0; // for HODL
        for (var y = 1; y <= compTimeframeYears; y++) {
          var calendarYear = startYear + y;
          var yBlockReward = halvingOn ? getBlockRewardForYear(calendarYear) : BLOCK_REWARD;
          var yPrice = isForecast ? compBtcPrice * Math.pow(1 + btcGrowthRate, y) : compBtcPrice;
          var yNetworkTH = isForecast ? compNetworkTH * Math.pow(1 + diffGrowthRate, y) : compNetworkTH;

          var yBtcPerDay = (totalHashrate / yNetworkTH) * BLOCKS_PER_DAY * yBlockReward;
          var yRevenuePerDay = yBtcPerDay * yPrice;
          var yPoolFeePerDay = yRevenuePerDay * (poolFeePercent / 100);
          var yDailyProfit = yRevenuePerDay - electricityPerDay - yPoolFeePerDay;

          miningEarnings += yDailyProfit * operatingDaysPerYear;
          totalRevenue += yRevenuePerDay * operatingDaysPerYear;
          totalElectricityCost += electricityPerDayFull * operatingDaysPerYear;
          totalPvSavings += pvSavingsPerDay * operatingDaysPerYear;
          totalPoolFees += yPoolFeePerDay * operatingDaysPerYear;
          totalBtcMined += yBtcPerDay * operatingDaysPerYear;
          totalBtcAccumulated += yBtcPerDay * operatingDaysPerYear;
        }

        // HODL: revalue all accumulated BTC at the final year's price
        if (hodlOn && compTimeframeYears > 0) {
          var finalPrice = compBtcPrice * Math.pow(1 + btcGrowthRate, compTimeframeYears);
          var hodlRevenue = totalBtcAccumulated * finalPrice;
          var hodlPoolFees = hodlRevenue * (poolFeePercent / 100);
          totalRevenue = hodlRevenue;
          totalPoolFees = hodlPoolFees;
          miningEarnings = hodlRevenue - totalElectricityCost + totalPvSavings - hodlPoolFees;
        }

        totalEarnings = miningEarnings + heatingSavings;

        // Break-even based on annual total earnings
        var annualTotalEarnings = totalEarnings / compTimeframeYears;
        if (totalPrice > 0 && annualTotalEarnings > 0) {
          breakeven = Math.ceil(totalPrice / (annualTotalEarnings / 12));
        }

        // ROI based on totalEarnings
        if (totalPrice > 0) {
          roi = (totalEarnings - totalPrice) / totalPrice * 100;
        }
      }

      rows.push({
        p: p, qty: qty, unitPrice: unitPrice, compareAtPrice: compareAtPrice, totalPrice: totalPrice,
        efficiency: efficiency, missingHashrate: missingHashrate, missingPower: missingPower, missingData: missingData,
        totalHashrate: totalHashrate, totalPower: totalPower,
        heatingSavings: heatingSavings, miningEarnings: miningEarnings,
        totalEarnings: totalEarnings, breakeven: breakeven, roi: roi,
        totalRevenue: totalRevenue, totalElectricityCost: totalElectricityCost,
        totalPvSavings: totalPvSavings,
        totalPoolFees: totalPoolFees, totalBtcMined: totalBtcMined,
        effectiveRate: effectiveRateVal, energyPerYear: energyPerYear,
        heatCoveragePct: heatCoveragePct, usefulHeatKwh: usefulHeatKwh, wastedHeatKwh: wastedHeatKwh,
        heatDemand: heatDemand,
        pvCoveragePct: pvCoveragePct, pvCoveredKwh: pvCoveredKwh
      });
    }

    // Sort rows: available first, then by best ROI
    rows.sort(function(a, b) {
      if (a.p.available !== b.p.available) return a.p.available ? -1 : 1;
      return b.roi - a.roi;
    });

    // --- Render cards ---
    if (elHeaterCards) {
      var cardsHtml = '';
      for (var i = 0; i < rows.length; i++) {
        var r = rows[i];
        var p = r.p;
        var qty = r.qty;

        cardsHtml += '<div class="bmc-heater-card">';

        // Header: image + info side by side
        cardsHtml += '<div class="bmc-heater-card__header">';
        if (p.image) {
          if (p.available && p.url !== '#') {
            cardsHtml += '<a href="' + p.url + '"><img src="' + p.image + '" alt="" width="60" height="60" loading="lazy" class="bmc-heater-card__image"></a>';
          } else {
            cardsHtml += '<img src="' + p.image + '" alt="" width="60" height="60" loading="lazy" class="bmc-heater-card__image">';
          }
        }
        cardsHtml += '<div class="bmc-heater-card__header-info">';
        if (p.available && p.url !== '#') {
          cardsHtml += '<div class="bmc-heater-card__title"><a href="' + p.url + '" class="bmc-heater-card__title-link">' + p.name + '</a></div>';
        } else {
          cardsHtml += '<div class="bmc-heater-card__title">' + p.name + '</div>';
        }
        cardsHtml += '<div class="bmc-heater-card__specs">' + (r.missingHashrate ? '— TH/s' : p.hashrate + ' TH/s') + ' · ' + (r.missingPower ? '— W' : formatNumber(p.power) + ' W') + '</div>';

        if (r.missingData) {
          var missingFields = [];
          if (r.missingHashrate) missingFields.push('TH/s');
          if (r.missingPower) missingFields.push('W');
          cardsHtml += '<div class="bmc-heater-card__missing-data">' + missingFields.join(' and ') + ' value is missing for this product.</div>';
          cardsHtml += '</div></div></div>';
          continue;
        }

        // Price row: discount + price + compare on one line
        cardsHtml += '<div class="bmc-heater-card__price-row">';
        if (r.totalPrice > 0) {
          if (p.onSale && r.compareAtPrice > 0) {
            var discountPct = Math.round((1 - r.unitPrice / r.compareAtPrice) * 100);
            if (qty > 1) {
              cardsHtml += '<span class="bmc-heater-card__discount">-' + discountPct + '%</span> ';
              cardsHtml += '<span class="bmc-heater-card__price">' + formatMoney(r.totalPrice) + '</span> ';
              cardsHtml += '<span class="bmc-heater-card__compare-price">' + formatMoney(r.compareAtPrice * qty) + '</span>';
              cardsHtml += '<span class="bmc-heater-card__price-sub">' + formatMoney(r.unitPrice) + ' \u00d7 ' + qty + '</span>';
            } else {
              cardsHtml += '<span class="bmc-heater-card__discount">-' + discountPct + '%</span> ';
              cardsHtml += '<span class="bmc-heater-card__price">' + formatMoney(r.unitPrice) + '</span> ';
              cardsHtml += '<span class="bmc-heater-card__compare-price">' + formatMoney(r.compareAtPrice) + '</span>';
            }
          } else {
            if (qty > 1) {
              cardsHtml += '<span class="bmc-heater-card__price">' + formatMoney(r.totalPrice) + '</span>';
              cardsHtml += '<span class="bmc-heater-card__price-sub">' + formatMoney(r.unitPrice) + ' \u00d7 ' + qty + '</span>';
            } else {
              cardsHtml += '<span class="bmc-heater-card__price">' + formatMoney(r.unitPrice) + '</span>';
            }
          }
        }
        cardsHtml += '</div>';
        // Qty stepper on its own row
        cardsHtml += '<div class="bmc-heater-card__qty">';
        cardsHtml += '<button type="button" class="bmc-heater-card__qty-btn" data-qty-key="' + p.key + '" data-qty-dir="-1">\u2212</button>';
        cardsHtml += '<span class="bmc-heater-card__qty-val">' + qty + '</span>';
        cardsHtml += '<button type="button" class="bmc-heater-card__qty-btn" data-qty-key="' + p.key + '" data-qty-dir="1">+</button>';
        cardsHtml += '</div>';
        // Bundle hint
        if (p.available && p.hasBundle && qty >= 2) {
          var bundlePct = qty === 2 ? 5 : qty === 3 ? 10 : 15;
          cardsHtml += '<span class="bmc-heater-card__bundle-hint">' + (labels.bundle_save || 'Save {{ pct }}% extra with bundle pricing').replace('{{ pct }}', bundlePct) + '</span>';
        } else if (p.available && p.hasBundle && qty === 1) {
          cardsHtml += '<span class="bmc-heater-card__bundle-hint">' + (labels.bundle_hint || 'Buy 2+ for bundle discounts') + '</span>';
        }
        cardsHtml += '</div></div>';

        // Heat coverage bar
        if (hasMiningData && r.heatDemand > 0) {
          cardsHtml += '<div class="bmc-heater-card__coverage">';
          cardsHtml += '<div class="bmc-heater-card__coverage-label"><span>' + (labels.comp_col_heat_coverage || 'Heat coverage') + '</span><span>' + Math.round(r.heatCoveragePct) + '%</span></div>';
          cardsHtml += '<div class="bmc-heater-card__coverage-bar"><div class="bmc-heater-card__coverage-fill" style="width:' + Math.min(100, r.heatCoveragePct) + '%"></div></div>';
          var coverageDetail = formatNumber(Math.round(r.usefulHeatKwh)) + ' / ' + formatNumber(Math.round(r.heatDemand)) + ' kWh';
          if (r.wastedHeatKwh > 0) {
            coverageDetail += ' · <span class="bmc-heater-card__wasted">' + formatNumber(Math.round(r.wastedHeatKwh)) + ' ' + (labels.kwh_excess || 'kWh excess') + '</span>';
          }
          cardsHtml += '<div class="bmc-heater-card__coverage-detail">' + coverageDetail + '</div>';
          cardsHtml += '</div>';
        }

        // Earnings breakdown (detailed waterfall)
        if (hasMiningData) {
          cardsHtml += '<div class="bmc-heater-card__breakdown">';
          cardsHtml += '<div class="bmc-heater-card__row"><span class="bmc-heater-card__row-label">' + (labels.mining_revenue || 'Mining revenue') + '</span><span class="bmc-heater-card__row-value">+' + formatMoney(r.totalRevenue) + '</span></div>';
          cardsHtml += '<div class="bmc-heater-card__row"><span class="bmc-heater-card__row-label">' + (labels.electricity_cost || 'Electricity cost') + '</span><span class="bmc-heater-card__row-value">-' + formatMoney(r.totalElectricityCost) + '</span></div>';
          if (r.totalPvSavings > 0) {
            cardsHtml += '<div class="bmc-heater-card__row"><span class="bmc-heater-card__row-label">' + (labels.pv_savings || 'PV savings') + '</span><span class="bmc-heater-card__row-value">+' + formatMoney(r.totalPvSavings) + '</span></div>';
          }
          cardsHtml += '<div class="bmc-heater-card__row"><span class="bmc-heater-card__row-label">' + (labels.pool_fees || 'Pool fees') + '</span><span class="bmc-heater-card__row-value">-' + formatMoney(r.totalPoolFees) + '</span></div>';
          cardsHtml += '<div class="bmc-heater-card__row"><span class="bmc-heater-card__row-label">' + (labels.comp_col_heating_savings || 'Heating savings') + '</span><span class="bmc-heater-card__row-value">+' + formatMoney(r.heatingSavings) + '</span></div>';
          var totalClass = r.totalEarnings >= 0 ? 'bmc-heater-card__row--positive' : 'bmc-heater-card__row--negative';
          cardsHtml += '<div class="bmc-heater-card__row bmc-heater-card__row--total ' + totalClass + '"><span class="bmc-heater-card__row-label">' + (labels.comp_col_total_earnings || 'Total earnings') + '</span><span class="bmc-heater-card__row-value">' + formatMoney(r.totalEarnings) + '<span class="bmc-heater-card__btc-sub">' + r.totalBtcMined.toFixed(4) + ' BTC</span></span></div>';
          cardsHtml += buildPills(r, compTimeframeYears);
          cardsHtml += '</div>';
        }

        // CTA: ATC button only
        cardsHtml += '<div class="bmc-heater-card__cta">';
        if (p.available && p.variantId) {
          cardsHtml += '<button type="button" class="button button--primary bmc-heater-card__atc" data-variant-id="' + p.variantId + '" data-qty="' + qty + '"><span>' + (labels.add_qty_to_cart || 'Add {{ qty }}x to Cart').replace('{{ qty }}', qty) + '</span></button>';
        } else if (p.available && p.url !== '#') {
          cardsHtml += '<a href="' + p.url + '" class="button button--primary"><span>' + (labels.view_product || 'View Product') + '</span></a>';
        } else {
          cardsHtml += '<button type="button" class="button button--primary" disabled><span>' + (labels.sold_out || 'Sold Out') + '</span></button>';
        }
        cardsHtml += '</div>';
        if (p.available && qty >= 2 && r.heatDemand > 0) {
          cardsHtml += '<span class="bmc-heater-card__qty-hint">' + (labels.qty_recommended || '{{ qty }} recommended based on your heat demand of {{ demand }} kWh/yr').replace('{{ qty }}', qty).replace('{{ demand }}', formatNumber(r.heatDemand)) + '</span>';
        }

        cardsHtml += '</div>'; // end card
      }
      elHeaterCards.innerHTML = cardsHtml;
    }

  }

  function calculateMiners() {
    if (!elMinerCards) return;

    var electricityCost = parseFloat(elCompElectricity.value) || 0;
    var poolFeePercent = parseFloat(elCompPoolFee.value) || 0;
    var compBtcPrice = getCompBtcPrice();
    var compNetworkTH = getCompNetworkHashrateTH();
    var hasMiningData = compBtcPrice > 0 && compNetworkTH > 0;

    var pvOn = elCompPvToggle && elCompPvToggle.checked;

    // PV parameters
    var pvSize = pvOn ? (parseFloat(elCompPvSize.value) || 0) : 0;
    var pvSunshine = pvOn ? (parseFloat(elCompPvSunshine.value) || 0) : 0;
    var pvAllocation = pvOn ? (parseFloat(elCompPvAllocation.value) || 0) : 0;
    var feedInTariff = pvOn ? (parseFloat(elCompFeedInTariff.value) || 0) : 0;

    // --- Pass 1: Calculate miner rows ---
    var rows = [];
    for (var i = 0; i < compProducts.length; i++) {
      var p = compProducts[i];
      if (p.isHeater) continue;

      var qty = compQuantities[p.key] || 1;

      var unitPrice = p.price > 0 ? convertEurToActive(p.price) : 0;
      var compareAtPrice = p.compareAtPrice > 0 ? convertEurToActive(p.compareAtPrice) : 0;
      var totalPrice = unitPrice * qty;
      var missingHashrate = !p.hashrate || p.hashrate <= 0;
      var missingPower = !p.power || p.power <= 0;
      var missingData = missingHashrate || missingPower;
      var efficiency = missingData ? '—' : (p.efficiencyJth > 0 ? p.efficiencyJth : p.power / p.hashrate).toFixed(1);

      var totalHashrate = p.hashrate * qty;
      var totalPower = p.power * qty;

      var dailyProfit = 0;
      var miningEarnings = 0;
      var totalEarnings = 0;
      var breakeven = -1;
      var roi = 0;
      var pvCoveragePct = 0;
      var pvCoveredKwh = 0;
      var totalRevenue = 0;
      var totalElecCost = 0;
      var totalPoolFees = 0;
      var totalBtcMined = 0;
      var heatingSavings = 0;
      var heatCoveragePct = 0;
      var usefulHeatKwh = 0;
      var wastedHeatKwh = 0;

      // Miner heating mode
      var minerHeatingOn = elCompMinerHeatingCheckbox && elCompMinerHeatingCheckbox.checked;
      var isHydro = p.name.toLowerCase().indexOf('hydro') !== -1 || p.productKey.toLowerCase().indexOf('hydro') !== -1;
      var isSoloMiner = p.isSoloMiner || false;
      var heatingApplies = minerHeatingOn && !isSoloMiner;
      var minerRuntime = (elCompMinerRuntime && elCompMinerRuntime.value) || 'year-round';
      var heatingCost = 0;
      var heatDemand = 0;
      var heatingMonths = 7;
      if (heatingApplies) {
        heatingCost = parseFloat(elCompHeatingCost.value) || 0;
        heatDemand = parseFloat(elCompHeatDemand.value) || 0;
        heatingMonths = elCompHeatingMonths ? (parseInt(elCompHeatingMonths.value, 10) || 7) : 7;
      }

      // Operating days: year-round (365) or season-only (only for non-hydro with heating)
      var operatingDaysPerYear = (heatingApplies && minerRuntime === 'season-only')
        ? (heatingMonths / 12) * 365
        : 365;
      var heatingDaysPerYear = (heatingMonths / 12) * 365;

      if (hasMiningData) {
        var dailyConsumption = (totalPower / 1000) * 24;
        var halvingOn = elCompHalvingToggle && elCompHalvingToggle.checked;
        var hodlOn = elCompHodlToggle && elCompHodlToggle.checked && forecastEnabled;
        var isForecast = forecastEnabled;
        var btcGrowthRate = isForecast ? (parseFloat(elCompBtcGrowth.value) || 0) / 100 : 0;
        var diffGrowthRate = isForecast ? (parseFloat(elCompDiffGrowth.value) || 0) / 100 : 0;
        var startYear = new Date().getFullYear();

        // Heating calculations (when miner heating is on, excluding hydro)
        if (heatingApplies) {
          var heatOutputDays = (minerRuntime === 'year-round') ? heatingDaysPerYear : operatingDaysPerYear;
          var heatOutputDuringHeating = dailyConsumption * heatOutputDays;
          if (heatDemand > 0) {
            heatCoveragePct = Math.min(100, (heatOutputDuringHeating / heatDemand) * 100);
          }
          usefulHeatKwh = heatDemand > 0 ? Math.min(heatDemand, heatOutputDuringHeating) : 0;
          wastedHeatKwh = Math.max(0, heatOutputDuringHeating - usefulHeatKwh);
          heatingSavings = heatingCost * usefulHeatKwh * compTimeframeYears;
        }

        // PV offset
        var gridKwh = dailyConsumption;
        if (pvOn && pvSize > 0 && pvSunshine > 0) {
          var dailyPvAvailable = pvSize * pvSunshine * (pvAllocation / 100);
          pvCoveredKwh = Math.min(dailyPvAvailable, dailyConsumption);
          gridKwh = dailyConsumption - pvCoveredKwh;
          pvCoveragePct = (pvCoveredKwh / dailyConsumption) * 100;
        }

        var electricityPerDayFull = dailyConsumption * electricityCost;
        var electricityPerDay = electricityPerDayFull;
        var pvSavingsPerDay = 0;
        if (pvOn && pvCoveredKwh > 0) {
          electricityPerDay = (gridKwh * electricityCost) + (pvCoveredKwh * feedInTariff);
          pvSavingsPerDay = electricityPerDayFull - electricityPerDay;
        }

        // Year-by-year calculation (supports halving + forecast + HODL)
        var totalBtcAccumulated = 0;
        var totalElecCost = 0;
        var totalPvSavings = 0;
        var totalRev = 0;
        var totalPool = 0;
        for (var y = 1; y <= compTimeframeYears; y++) {
          var calendarYear = startYear + y;
          var yBlockReward = halvingOn ? getBlockRewardForYear(calendarYear) : BLOCK_REWARD;
          var yPrice = isForecast ? compBtcPrice * Math.pow(1 + btcGrowthRate, y) : compBtcPrice;
          var yNetworkTH = isForecast ? compNetworkTH * Math.pow(1 + diffGrowthRate, y) : compNetworkTH;

          var yBtcPerDay = (totalHashrate / yNetworkTH) * BLOCKS_PER_DAY * yBlockReward;
          var yRevenuePerDay = yBtcPerDay * yPrice;
          var yPoolFeePerDay = yRevenuePerDay * (poolFeePercent / 100);
          var yDailyProfit = yRevenuePerDay - electricityPerDay - yPoolFeePerDay;

          miningEarnings += yDailyProfit * operatingDaysPerYear;
          totalRev += yRevenuePerDay * operatingDaysPerYear;
          totalElecCost += electricityPerDayFull * operatingDaysPerYear;
          totalPvSavings += pvSavingsPerDay * operatingDaysPerYear;
          totalPool += yPoolFeePerDay * operatingDaysPerYear;
          totalBtcAccumulated += yBtcPerDay * operatingDaysPerYear;
        }

        // HODL: revalue all BTC at final year's price
        if (hodlOn && compTimeframeYears > 0) {
          var finalPrice = compBtcPrice * Math.pow(1 + btcGrowthRate, compTimeframeYears);
          var hodlRevenue = totalBtcAccumulated * finalPrice;
          var hodlPoolFees = hodlRevenue * (poolFeePercent / 100);
          totalRev = hodlRevenue;
          totalPool = hodlPoolFees;
          miningEarnings = hodlRevenue - totalElecCost + totalPvSavings - hodlPoolFees;
        }

        totalEarnings = miningEarnings + heatingSavings;
        totalBtcMined = totalBtcAccumulated;

        // Break-even based on annual earnings
        var annualEarnings = totalEarnings / compTimeframeYears;
        if (totalPrice > 0 && annualEarnings > 0) {
          breakeven = Math.ceil(totalPrice / (annualEarnings / 12));
        }

        // ROI
        if (totalPrice > 0) {
          roi = (totalEarnings - totalPrice) / totalPrice * 100;
        }
      }

      rows.push({
        p: p, qty: qty, unitPrice: unitPrice, compareAtPrice: compareAtPrice,
        totalPrice: totalPrice,
        efficiency: efficiency, missingHashrate: missingHashrate, missingPower: missingPower, missingData: missingData,
        totalHashrate: totalHashrate, totalPower: totalPower,
        miningEarnings: miningEarnings, heatingSavings: heatingSavings,
        totalEarnings: totalEarnings, breakeven: breakeven, roi: roi,
        totalRevenue: totalRev, totalElectricityCost: totalElecCost, totalPvSavings: totalPvSavings, totalPoolFees: totalPool,
        totalBtcMined: totalBtcMined,
        heatCoveragePct: heatCoveragePct, usefulHeatKwh: usefulHeatKwh, wastedHeatKwh: wastedHeatKwh,
        heatDemand: heatingApplies ? (heatDemand || 0) : 0,
        minerHeatingOn: minerHeatingOn,
        heatingApplies: heatingApplies,
        isHydro: isHydro,
        isSoloMiner: isSoloMiner,
        pvCoveragePct: pvCoveragePct, pvCoveredKwh: pvCoveredKwh
      });
    }

    // Sort rows: available first, then by best ROI
    rows.sort(function(a, b) {
      if (a.p.available !== b.p.available) return a.p.available ? -1 : 1;
      return b.roi - a.roi;
    });

    // --- Render miner cards ---
    if (elMinerCards) {
      var cardsHtml = '';
      var minerHeatingOn = elCompMinerHeatingCheckbox && elCompMinerHeatingCheckbox.checked;

      for (var i = 0; i < rows.length; i++) {
        var r = rows[i];
        var p = r.p;
        var qty = r.qty;

        var soldOutClass = !p.available ? ' bmc-miner-card--sold-out' : '';

        // When heating is ON, use heater-style vertical card layout
        if (minerHeatingOn) {
          cardsHtml += '<div class="bmc-heater-card' + soldOutClass + '">';

          // Header: image + info
          cardsHtml += '<div class="bmc-heater-card__header">';
          if (p.image) {
            if (p.available && p.url !== '#') {
              cardsHtml += '<a href="' + p.url + '"><img src="' + p.image + '" alt="" width="60" height="60" loading="lazy" class="bmc-heater-card__image"></a>';
            } else {
              cardsHtml += '<img src="' + p.image + '" alt="" width="60" height="60" loading="lazy" class="bmc-heater-card__image">';
            }
          }
          cardsHtml += '<div class="bmc-heater-card__header-info">';
          if (p.available && p.url !== '#') {
            cardsHtml += '<div class="bmc-heater-card__title"><a href="' + p.url + '" class="bmc-heater-card__title-link">' + p.name + '</a></div>';
          } else {
            cardsHtml += '<div class="bmc-heater-card__title">' + p.name + '</div>';
          }
          cardsHtml += '<div class="bmc-heater-card__specs">' + (r.missingHashrate ? '— TH/s' : p.hashrate + ' TH/s') + ' · ' + (r.missingPower ? '— W' : formatNumber(p.power) + ' W') + (r.missingData ? '' : ' · ' + r.efficiency + ' J/TH') + '</div>';

          if (r.missingData) {
            var missingFields = [];
            if (r.missingHashrate) missingFields.push('TH/s');
            if (r.missingPower) missingFields.push('W');
            cardsHtml += '<div class="bmc-heater-card__missing-data">' + missingFields.join(' and ') + ' value is missing for this product.</div>';
            cardsHtml += '</div></div></div>';
            continue;
          }

          // Price row
          cardsHtml += '<div class="bmc-heater-card__price-row">';
          if (r.totalPrice > 0) {
            if (p.onSale && r.compareAtPrice > 0) {
              var discountPct = Math.round((1 - r.unitPrice / r.compareAtPrice) * 100);
              if (qty > 1) {
                cardsHtml += '<span class="bmc-heater-card__discount">-' + discountPct + '%</span> ';
                cardsHtml += '<span class="bmc-heater-card__price">' + formatMoney(r.totalPrice) + '</span> ';
                cardsHtml += '<span class="bmc-heater-card__compare-price">' + formatMoney(r.compareAtPrice * qty) + '</span>';
                cardsHtml += '<span class="bmc-heater-card__price-sub">' + formatMoney(r.unitPrice) + ' \u00d7 ' + qty + '</span>';
              } else {
                cardsHtml += '<span class="bmc-heater-card__discount">-' + discountPct + '%</span> ';
                cardsHtml += '<span class="bmc-heater-card__price">' + formatMoney(r.unitPrice) + '</span> ';
                cardsHtml += '<span class="bmc-heater-card__compare-price">' + formatMoney(r.compareAtPrice) + '</span>';
              }
            } else {
              if (qty > 1) {
                cardsHtml += '<span class="bmc-heater-card__price">' + formatMoney(r.totalPrice) + '</span>';
                cardsHtml += '<span class="bmc-heater-card__price-sub">' + formatMoney(r.unitPrice) + ' \u00d7 ' + qty + '</span>';
              } else {
                cardsHtml += '<span class="bmc-heater-card__price">' + formatMoney(r.unitPrice) + '</span>';
              }
            }
          }
          cardsHtml += '</div>';
          // Qty stepper
          cardsHtml += '<div class="bmc-heater-card__qty">';
          cardsHtml += '<button type="button" class="bmc-heater-card__qty-btn" data-qty-key="' + p.key + '" data-qty-dir="-1">\u2212</button>';
          cardsHtml += '<span class="bmc-heater-card__qty-val">' + qty + '</span>';
          cardsHtml += '<button type="button" class="bmc-heater-card__qty-btn" data-qty-key="' + p.key + '" data-qty-dir="1">+</button>';
          cardsHtml += '</div>';
          cardsHtml += '</div></div>';

          // Heat coverage bar (or solo notice)
          if (hasMiningData && r.isSoloMiner) {
            var soloNote = (labels.solo_heating_note || 'Solo miners produce very little heat. You would need a large number to cover your heating demand. Consider our [Bitcoin Heaters] for efficient home heating.');
            soloNote = soloNote.replace(/\[([^\]]+)\]/, '<button type="button" class="bmc-calc__heating-warning-link bmc-calc__view-heaters-link">$1</button>');
            cardsHtml += '<div class="bmc-heater-card__coverage bmc-heater-card__coverage--disabled">';
            cardsHtml += '<div class="bmc-heater-card__coverage-label"><span>' + (labels.comp_col_heat_coverage || 'Heat coverage') + '</span><span>—</span></div>';
            cardsHtml += '<div class="bmc-heater-card__coverage-bar"><div class="bmc-heater-card__coverage-fill" style="width:0%"></div></div>';
            cardsHtml += '<div class="bmc-heater-card__hydro-note">' + soloNote + '</div>';
            cardsHtml += '</div>';
          } else if (hasMiningData && r.heatDemand > 0) {
            cardsHtml += '<div class="bmc-heater-card__coverage">';
            cardsHtml += '<div class="bmc-heater-card__coverage-label"><span>' + (labels.comp_col_heat_coverage || 'Heat coverage') + '</span><span>' + Math.round(r.heatCoveragePct) + '%</span></div>';
            cardsHtml += '<div class="bmc-heater-card__coverage-bar"><div class="bmc-heater-card__coverage-fill" style="width:' + Math.min(100, r.heatCoveragePct) + '%"></div></div>';
            var coverageDetail = formatNumber(Math.round(r.usefulHeatKwh)) + ' / ' + formatNumber(Math.round(r.heatDemand)) + ' kWh';
            if (r.wastedHeatKwh > 0) {
              coverageDetail += ' · <span class="bmc-heater-card__wasted">' + formatNumber(Math.round(r.wastedHeatKwh)) + ' ' + (labels.kwh_excess || 'kWh excess') + '</span>';
            }
            cardsHtml += '<div class="bmc-heater-card__coverage-detail">' + coverageDetail + '</div>';
            if (r.isHydro) {
              var hydroNote = (labels.hydro_heating_note || 'Requires integration with a hydronic heating system (underfloor heating, radiators).');
              cardsHtml += '<div class="bmc-heater-card__hydro-note">' + hydroNote + '</div>';
            }
            cardsHtml += '</div>';
          }

          // Earnings breakdown
          if (hasMiningData) {
            cardsHtml += '<div class="bmc-heater-card__breakdown">';
            cardsHtml += '<div class="bmc-heater-card__row"><span class="bmc-heater-card__row-label">' + (labels.mining_revenue || 'Mining revenue') + '</span><span class="bmc-heater-card__row-value">+' + formatMoney(r.totalRevenue) + '</span></div>';
            cardsHtml += '<div class="bmc-heater-card__row"><span class="bmc-heater-card__row-label">' + (labels.electricity_cost || 'Electricity cost') + '</span><span class="bmc-heater-card__row-value">-' + formatMoney(r.totalElectricityCost) + '</span></div>';
            cardsHtml += '<div class="bmc-heater-card__row"><span class="bmc-heater-card__row-label">' + (labels.pool_fees || 'Pool fees') + '</span><span class="bmc-heater-card__row-value">-' + formatMoney(r.totalPoolFees) + '</span></div>';
            if (r.totalPvSavings > 0) {
              cardsHtml += '<div class="bmc-heater-card__row"><span class="bmc-heater-card__row-label">' + (labels.pv_savings || 'PV savings') + '</span><span class="bmc-heater-card__row-value">+' + formatMoney(r.totalPvSavings) + '</span></div>';
            }
            if (r.heatingSavings > 0) {
              cardsHtml += '<div class="bmc-heater-card__row"><span class="bmc-heater-card__row-label">' + (labels.comp_col_heating_savings || 'Heating savings') + '</span><span class="bmc-heater-card__row-value">+' + formatMoney(r.heatingSavings) + '</span></div>';
            }
            var totalClass = r.totalEarnings >= 0 ? 'bmc-heater-card__row--positive' : 'bmc-heater-card__row--negative';
            cardsHtml += '<div class="bmc-heater-card__row bmc-heater-card__row--total ' + totalClass + '"><span class="bmc-heater-card__row-label">' + (labels.comp_col_total_earnings || 'Total earnings') + '</span><span class="bmc-heater-card__row-value">' + formatMoney(r.totalEarnings) + '<span class="bmc-heater-card__btc-sub">' + r.totalBtcMined.toFixed(4) + ' BTC</span></span></div>';
            // Break-even + ROI pills
            cardsHtml += buildPills(r, compTimeframeYears);
            cardsHtml += '</div>';
          }

          // CTA
          cardsHtml += '<div class="bmc-heater-card__cta">';
          if (p.available && p.variantId) {
            cardsHtml += '<button type="button" class="button button--primary bmc-heater-card__atc" data-variant-id="' + p.variantId + '" data-qty="' + qty + '"><span>' + (labels.add_qty_to_cart || 'Add {{ qty }}x to Cart').replace('{{ qty }}', qty) + '</span></button>';
          } else if (p.available && p.url !== '#') {
            cardsHtml += '<a href="' + p.url + '" class="button button--primary"><span>' + (labels.view_product || 'View Product') + '</span></a>';
          } else {
            cardsHtml += '<button type="button" class="button button--primary" disabled><span>' + (labels.sold_out || 'Sold Out') + '</span></button>';
          }
          cardsHtml += '</div>';
          if (r.heatingApplies && qty >= 2 && r.heatDemand > 0) {
            cardsHtml += '<span class="bmc-heater-card__qty-hint">' + (labels.qty_recommended || '{{ qty }} recommended based on your heat demand of {{ demand }} kWh/yr').replace('{{ qty }}', qty).replace('{{ demand }}', formatNumber(r.heatDemand)) + '</span>';
          }

          cardsHtml += '</div>'; // end heater-style card

        } else {
          // Standard miner card (no heating) — same vertical layout without heating sections
          cardsHtml += '<div class="bmc-heater-card' + soldOutClass + '">';

          // Header: image + info
          cardsHtml += '<div class="bmc-heater-card__header">';
          if (p.image) {
            if (p.available && p.url !== '#') {
              cardsHtml += '<a href="' + p.url + '"><img src="' + p.image + '" alt="" width="60" height="60" loading="lazy" class="bmc-heater-card__image"></a>';
            } else {
              cardsHtml += '<img src="' + p.image + '" alt="" width="60" height="60" loading="lazy" class="bmc-heater-card__image">';
            }
          }
          cardsHtml += '<div class="bmc-heater-card__header-info">';
          if (p.available && p.url !== '#') {
            cardsHtml += '<div class="bmc-heater-card__title"><a href="' + p.url + '" class="bmc-heater-card__title-link">' + p.name + '</a></div>';
          } else {
            cardsHtml += '<div class="bmc-heater-card__title">' + p.name + '</div>';
          }
          cardsHtml += '<div class="bmc-heater-card__specs">' + (r.missingHashrate ? '— TH/s' : p.hashrate + ' TH/s') + ' · ' + (r.missingPower ? '— W' : formatNumber(p.power) + ' W') + (r.missingData ? '' : ' · ' + r.efficiency + ' J/TH') + '</div>';

          if (r.missingData) {
            var missingFields = [];
            if (r.missingHashrate) missingFields.push('TH/s');
            if (r.missingPower) missingFields.push('W');
            cardsHtml += '<div class="bmc-heater-card__missing-data">' + missingFields.join(' and ') + ' value is missing for this product.</div>';
            cardsHtml += '</div></div></div>';
            continue;
          }

          // Price row
          cardsHtml += '<div class="bmc-heater-card__price-row">';
          if (r.totalPrice > 0) {
            if (p.onSale && r.compareAtPrice > 0) {
              var discountPct = Math.round((1 - r.unitPrice / r.compareAtPrice) * 100);
              if (qty > 1) {
                cardsHtml += '<span class="bmc-heater-card__discount">-' + discountPct + '%</span> ';
                cardsHtml += '<span class="bmc-heater-card__price">' + formatMoney(r.totalPrice) + '</span> ';
                cardsHtml += '<span class="bmc-heater-card__compare-price">' + formatMoney(r.compareAtPrice * qty) + '</span>';
                cardsHtml += '<span class="bmc-heater-card__price-sub">' + formatMoney(r.unitPrice) + ' \u00d7 ' + qty + '</span>';
              } else {
                cardsHtml += '<span class="bmc-heater-card__discount">-' + discountPct + '%</span> ';
                cardsHtml += '<span class="bmc-heater-card__price">' + formatMoney(r.unitPrice) + '</span> ';
                cardsHtml += '<span class="bmc-heater-card__compare-price">' + formatMoney(r.compareAtPrice) + '</span>';
              }
            } else {
              if (qty > 1) {
                cardsHtml += '<span class="bmc-heater-card__price">' + formatMoney(r.totalPrice) + '</span>';
                cardsHtml += '<span class="bmc-heater-card__price-sub">' + formatMoney(r.unitPrice) + ' \u00d7 ' + qty + '</span>';
              } else {
                cardsHtml += '<span class="bmc-heater-card__price">' + formatMoney(r.unitPrice) + '</span>';
              }
            }
          }
          cardsHtml += '</div>';
          // Qty stepper
          cardsHtml += '<div class="bmc-heater-card__qty">';
          cardsHtml += '<button type="button" class="bmc-heater-card__qty-btn" data-qty-key="' + p.key + '" data-qty-dir="-1">\u2212</button>';
          cardsHtml += '<span class="bmc-heater-card__qty-val">' + qty + '</span>';
          cardsHtml += '<button type="button" class="bmc-heater-card__qty-btn" data-qty-key="' + p.key + '" data-qty-dir="1">+</button>';
          cardsHtml += '</div>';
          cardsHtml += '</div></div>';

          // Earnings breakdown (without heating)
          if (hasMiningData) {
            cardsHtml += '<div class="bmc-heater-card__breakdown">';
            cardsHtml += '<div class="bmc-heater-card__row"><span class="bmc-heater-card__row-label">' + (labels.mining_revenue || 'Mining revenue') + '</span><span class="bmc-heater-card__row-value">+' + formatMoney(r.totalRevenue) + '</span></div>';
            cardsHtml += '<div class="bmc-heater-card__row"><span class="bmc-heater-card__row-label">' + (labels.electricity_cost || 'Electricity cost') + '</span><span class="bmc-heater-card__row-value">-' + formatMoney(r.totalElectricityCost) + '</span></div>';
            cardsHtml += '<div class="bmc-heater-card__row"><span class="bmc-heater-card__row-label">' + (labels.pool_fees || 'Pool fees') + '</span><span class="bmc-heater-card__row-value">-' + formatMoney(r.totalPoolFees) + '</span></div>';
            if (r.totalPvSavings > 0) {
              cardsHtml += '<div class="bmc-heater-card__row"><span class="bmc-heater-card__row-label">' + (labels.pv_savings || 'PV savings') + '</span><span class="bmc-heater-card__row-value">+' + formatMoney(r.totalPvSavings) + '</span></div>';
            }
            var totalClass = r.totalEarnings >= 0 ? 'bmc-heater-card__row--positive' : 'bmc-heater-card__row--negative';
            cardsHtml += '<div class="bmc-heater-card__row bmc-heater-card__row--total ' + totalClass + '"><span class="bmc-heater-card__row-label">' + (labels.comp_col_total_earnings || 'Total earnings') + '</span><span class="bmc-heater-card__row-value">' + formatMoney(r.totalEarnings) + '<span class="bmc-heater-card__btc-sub">' + r.totalBtcMined.toFixed(4) + ' BTC</span></span></div>';
            // Break-even + ROI pills
            cardsHtml += buildPills(r, compTimeframeYears);
            cardsHtml += '</div>';
          }

          // CTA
          cardsHtml += '<div class="bmc-heater-card__cta">';
          if (p.available && p.variantId) {
            cardsHtml += '<button type="button" class="button button--primary bmc-heater-card__atc" data-variant-id="' + p.variantId + '" data-qty="' + qty + '"><span>' + (qty > 1 ? (labels.add_qty_to_cart || 'Add {{ qty }}x to Cart').replace('{{ qty }}', qty) : (labels.add_to_cart || 'Add to Cart')) + '</span></button>';
          } else if (p.available && p.url !== '#') {
            cardsHtml += '<a href="' + p.url + '" class="button button--primary"><span>' + (labels.view_product || 'View Product') + '</span></a>';
          } else {
            cardsHtml += '<button type="button" class="button button--primary" disabled><span>' + (labels.sold_out || 'Sold Out') + '</span></button>';
          }
          cardsHtml += '</div>';

          cardsHtml += '</div>'; // end vertical miner card

        }
      }
      elMinerCards.innerHTML = cardsHtml;
      elMinerCards.classList.add('bmc-miner__cards--heating');
    }

  }

  function calculateComparison() {
    calculateHeaters();
    calculateMiners();

    // Update disclaimer
    var compBtcPrice = getCompBtcPrice();
    var compNetworkTH = getCompNetworkHashrateTH();
    var hasMiningData = compBtcPrice > 0 && compNetworkTH > 0;
    var pvOn = elCompPvToggle && elCompPvToggle.checked;
    var pvSize = pvOn ? (parseFloat(elCompPvSize.value) || 0) : 0;
    var pvSunshine = pvOn ? (parseFloat(elCompPvSunshine.value) || 0) : 0;
    var heatDemand = parseFloat(elCompHeatDemand.value) || 0;
    var heatingCost = parseFloat(elCompHeatingCost.value) || 0;

    if (elCompDisclaimer && hasMiningData) {
      var nhFormatted = (compNetworkTH / 1e6).toFixed(2);
      var diffFormatted = (getCompDifficulty() / 1e12).toFixed(2);
      var electricityCostVal = parseFloat(elCompElectricity.value) || 0;
      var heatingMonthsVal = elCompHeatingMonths ? (parseInt(elCompHeatingMonths.value, 10) || 7) : 7;
      var halvingOn = elCompHalvingToggle && elCompHalvingToggle.checked;
      var hodlOn = elCompHodlToggle && elCompHodlToggle.checked;
      var operatingDaysPerYear = (heatingMonthsVal / 12) * 365;

      var poolFeeVal = parseFloat(elCompPoolFee.value) || 0;
      var btcGrowthVal = elCompBtcGrowth ? (elCompBtcGrowth.value || '0') : '0';
      var diffGrowthVal = elCompDiffGrowth ? (elCompDiffGrowth.value || '0') : '0';

      var isHeaters = compActiveView === 'heaters';
      var minerHeatingOn = compActiveView === 'miners' && elCompMinerHeatingCheckbox && elCompMinerHeatingCheckbox.checked;
      var showHeating = isHeaters || minerHeatingOn;
      var minerRuntime = (elCompMinerRuntime && elCompMinerRuntime.value) || 'year-round';
      var minerOperatingDays = (minerHeatingOn && minerRuntime === 'season-only') ? Math.round((heatingMonthsVal / 12) * 365) : 365;

      var hodlFinalPrice = '';
      if (hodlOn && forecastEnabled) {
        hodlFinalPrice = formatMoney(compBtcPrice * Math.pow(1 + (parseFloat(btcGrowthVal) / 100), compTimeframeYears));
      }

      var bmcVersion = window.bmc2Version || '1.0.0';
      var html = '<span class="bmc-calc__version">21energy Bitcoin Mining Calculator v' + bmcVersion + '</span>';
      html += '<span class="bmc-calc__disclaimer-note">' + (labels.disclaimer_full || 'These calculations are estimates based on current network conditions. Actual earnings may vary. This is not financial advice.') + '</span>';
      html += '<details>';
      html += '<summary>' + (labels.how_calculated || 'How it\'s calculated') + '</summary>';
      html += '<div class="bmc-calc__disclaimer-list">';
      html += buildCalcDetails({
        used: { timePeriod: true, minerSpecs: false, heating: showHeating, forecast: forecastEnabled, halvings: halvingOn, hodl: hodlOn && forecastEnabled, pv: pvOn, historical: false },
        timePeriod: compTimeframeYears + ' ' + (labels.unit_years || 'years'), timePeriodYears: compTimeframeYears,
        btcPrice: formatMoney(compBtcPrice),
        hashrate: nhFormatted,
        difficulty: diffFormatted,
        blockReward: BLOCK_REWARD,
        minerHashrate: '—',
        minerPower: '—',
        electricityCost: electricityCostVal.toFixed(2),
        heatingCost: heatingCost.toFixed(2),
        heatDemand: formatNumber(heatDemand),
        heatingMonths: heatingMonthsVal,
        heatingDays: isHeaters ? Math.round(operatingDaysPerYear) : Math.round((heatingMonthsVal / 12) * 365),
        btcGrowth: btcGrowthVal,
        diffGrowth: diffGrowthVal,
        pvSize: pvSize,
        pvSunshine: pvSunshine,
        feedInTariff: (parseFloat(elCompFeedInTariff ? elCompFeedInTariff.value : 0) || 0).toFixed(2),
        poolFee: poolFeeVal,
        operatingDays: isHeaters ? Math.round(operatingDaysPerYear) : minerOperatingDays,
        currSymbol: activeCurrencySymbol,
        hodlPrice: hodlFinalPrice
      });
      html += '</div></details>';

      // Preserve open state of details elements
      var details = elCompDisclaimer.querySelectorAll('details');
      var openStates = [];
      for (var d = 0; d < details.length; d++) {
        openStates.push(details[d].open);
      }
      elCompDisclaimer.innerHTML = html;
      var newDetails = elCompDisclaimer.querySelectorAll('details');
      for (var d = 0; d < newDetails.length && d < openStates.length; d++) {
        newDetails[d].open = openStates[d];
      }
    }

    // Sync BTC price display (only in current mode)
    if (compPriceMode === 'current' && elCompBtcPrice && btcPrice > 0 && document.activeElement !== elCompBtcPrice) {
      elCompBtcPrice.value = formatPriceForInput(btcPrice);
      autoSizeCompPriceInput();
      updateCompPriceBadge();
    }
    // Update average display
    if ((compPriceMode === 'avg4y' || compPriceMode === 'avg1y') && elCompAvgDisplay) {
      var compBtcPriceForAvg = getCompBtcPrice();
      elCompAvgDisplay.value = compBtcPriceForAvg > 0 ? formatPriceForInput(compBtcPriceForAvg) : '...';
      if (elCompAvgSuffix) elCompAvgSuffix.textContent = activeCurrencySymbol;
    }

    // Update currency suffixes
    if (elCompPriceSuffix) {
      elCompPriceSuffix.textContent = activeCurrencySymbol;
    }
    if (elCompCustomPriceSuffix) {
      elCompCustomPriceSuffix.textContent = activeCurrencySymbol;
    }
    if (elCompElectricitySuffix) {
      elCompElectricitySuffix.textContent = activeCurrencySymbol + '/kWh';
    }
    if (elCompHeatingCostSuffix) {
      elCompHeatingCostSuffix.textContent = activeCurrencySymbol + '/kWh';
    }
    if (elCompFeedInSuffix) {
      elCompFeedInSuffix.textContent = activeCurrencySymbol + '/kWh';
    }
  }

  // No-op stubs kept for call-site compatibility
  function autoSizeCompPriceInput() {}
  function updateCompPriceBadge() {}

  function initComparison() {
    if (!elHeaterCards && !elMinerCards) return;

    // Quantity stepper — event delegation on both tables
    function handleQtyClick(e) {
      var btn = e.target.closest('[data-qty-key]');
      if (!btn) return;
      var key = btn.getAttribute('data-qty-key');
      var dir = parseInt(btn.getAttribute('data-qty-dir'), 10);
      var current = compQuantities[key] || 1;
      var next = Math.max(1, current + dir);
      compQuantities[key] = next;
      calculateComparison();
    }
    if (elMinerCards) {
      elMinerCards.addEventListener('click', handleQtyClick);
      elMinerCards.addEventListener('click', function(e) {
        var atcBtn = e.target.closest('.bmc-miner-card__atc') || e.target.closest('.bmc-heater-card__atc');
        if (!atcBtn) return;
        var variantId = atcBtn.getAttribute('data-variant-id');
        var qty = parseInt(atcBtn.getAttribute('data-qty'), 10) || 1;
        if (!variantId) return;
        var btnSpan = atcBtn.querySelector('span');
        var isHeaterStyle = atcBtn.classList.contains('bmc-heater-card__atc');
        atcBtn.disabled = true;
        if (btnSpan) btnSpan.textContent = labels.adding || 'Adding...';
        addToCart(parseInt(variantId, 10), qty)
          .catch(function() { window.location.href = '/cart'; })
          .finally(function() {
            atcBtn.disabled = false;
            if (btnSpan) btnSpan.textContent = isHeaterStyle
              ? (labels.add_qty_to_cart || 'Add {{ qty }}x to Cart').replace('{{ qty }}', qty)
              : (labels.add_to_cart || 'Add to Cart');
          });
      });
    }
    if (elHeaterCards) {
      elHeaterCards.addEventListener('click', handleQtyClick);
      elHeaterCards.addEventListener('click', function(e) {
        var atcBtn = e.target.closest('.bmc-heater-card__atc');
        if (!atcBtn) return;
        var variantId = atcBtn.getAttribute('data-variant-id');
        var qty = parseInt(atcBtn.getAttribute('data-qty'), 10) || 1;
        if (!variantId) return;
        var btnSpan = atcBtn.querySelector('span');
        atcBtn.disabled = true;
        if (btnSpan) btnSpan.textContent = labels.adding || 'Adding...';
        addToCart(parseInt(variantId, 10), qty)
          .catch(function() { window.location.href = '/cart'; })
          .finally(function() {
            atcBtn.disabled = false;
            if (btnSpan) btnSpan.textContent = (labels.add_qty_to_cart || 'Add {{ qty }}x to Cart').replace('{{ qty }}', qty);
          });
      });
    }

    // Settings inputs
    if (elCompElectricity) elCompElectricity.addEventListener('input', calculateComparison);
    if (elCompHeatingCost) elCompHeatingCost.addEventListener('input', calculateComparison);
    if (elCompPoolFee) elCompPoolFee.addEventListener('input', calculateComparison);

    // Toggle: Halving
    if (elCompHalvingToggle) {
      elCompHalvingToggle.addEventListener('change', function() {
        updateHalvingHint();
        calculateComparison();
      });
    }

    // Toggle: HODL
    if (elCompHodlToggle) {
      elCompHodlToggle.addEventListener('change', function() {
        updateHodlHint();
        calculateComparison();
      });
    }

    // Toggle: Miner heating
    if (elCompMinerHeatingCheckbox) {
      elCompMinerHeatingCheckbox.addEventListener('change', function() {
        updateMinerHeatingVisibility();
        if (elCompMinerHeatingCheckbox.checked) {
          setDefaultMinerHeatingQuantities();
        } else {
          resetMinerQuantities();
        }
        calculateComparison();
      });
    }
    // Miner runtime dropdown
    if (elCompMinerRuntime) {
      elCompMinerRuntime.addEventListener('change', calculateComparison);
    }
    // Miner heating warning "View Heaters" button
    // "View Heaters" links (sidebar warning + card notes)
    var elMinerHeatingViewHeatersBtn = document.getElementById('bmcMinerHeatingViewHeatersBtn');
    function switchToHeaters() {
      if (elCompProductType) {
        elCompProductType.value = 'heaters';
        elCompProductType.dispatchEvent(new Event('change'));
      }
    }
    if (elMinerHeatingViewHeatersBtn) {
      elMinerHeatingViewHeatersBtn.addEventListener('click', switchToHeaters);
    }
    if (elMinerCards) {
      elMinerCards.addEventListener('click', function(e) {
        if (e.target.closest('.bmc-calc__view-heaters-link')) {
          switchToHeaters();
        }
      });
    }

    // Toggle: PV
    if (elCompPvToggle && elCompPvGroup) {
      elCompPvToggle.addEventListener('change', function() {
        toggleGroup(elCompPvToggle, elCompPvGroup);
        calculateComparison();
      });
    }

    // Toggle sub-field inputs
    if (elCompHeatDemand) elCompHeatDemand.addEventListener('input', function() { setDefaultHeaterQuantities(); if (elCompMinerHeatingCheckbox && elCompMinerHeatingCheckbox.checked) setDefaultMinerHeatingQuantities(); calculateComparison(); updateHeatingMonthsHint(); });
    if (elCompHeatingMonths) elCompHeatingMonths.addEventListener('input', function() { setDefaultHeaterQuantities(); if (elCompMinerHeatingCheckbox && elCompMinerHeatingCheckbox.checked) setDefaultMinerHeatingQuantities(); calculateComparison(); updateHeatingMonthsHint(); updateHeaterSeasonHint(); });
    if (elCompPvSize) elCompPvSize.addEventListener('input', calculateComparison);
    if (elCompPvSunshine) elCompPvSunshine.addEventListener('input', calculateComparison);
    if (elCompPvAllocation) elCompPvAllocation.addEventListener('input', calculateComparison);
    if (elCompFeedInTariff) elCompFeedInTariff.addEventListener('input', calculateComparison);

    // Currency selector in sidebar
    if (elCompCurrency) {
      elCompCurrency.value = activeCurrency;
      elCompCurrency.addEventListener('change', function() {
        switchCurrency(elCompCurrency.value);
      });
    }

    // Recalculate when tab becomes visible (picks up changes from other tabs)
    var elCompTab = document.getElementById('bmcTabBtnComparison');
    if (elCompTab) {
      elCompTab.addEventListener('click', calculateComparison);
    }

    // Price mode selector
    if (elCompPriceMode) {
      elCompPriceMode.addEventListener('change', function() {
        switchPriceMode(elCompPriceMode.value);
      });
    }

    // Forecast edit button
    if (elCompForecastEditBtn && elCompForecastInputs) {
      elCompForecastEditBtn.addEventListener('click', function() {
        var isOpen = !elCompForecastInputs.hidden;
        elCompForecastInputs.hidden = isOpen;
        elCompForecastEditBtn.textContent = isOpen ? (labels.edit || 'Edit') : (labels.close || 'Close');
      });
    }

    // Forecast toggle
    if (elCompForecastToggle && elCompForecastGroup) {
      elCompForecastToggle.addEventListener('change', function() {
        forecastEnabled = elCompForecastToggle.checked;
        elCompForecastGroup.hidden = !forecastEnabled;
        // Show/hide HODL
        if (elCompHodlRow) elCompHodlRow.hidden = !forecastEnabled;
        if (elCompHodlHint) elCompHodlHint.hidden = !forecastEnabled;
        updateHalvingHint();
        updateHodlHint();
        calculateComparison();
      });
    }
    function updateForecastSummary() {
      if (!elCompForecastSummary) return;
      var btcG = elCompBtcGrowth ? (elCompBtcGrowth.value || '40') : '40';
      var diffG = elCompDiffGrowth ? (elCompDiffGrowth.value || '15') : '15';
      elCompForecastSummary.textContent = (labels.forecast_summary || 'BTC +[btc]%/yr · Diff +[diff]%/yr').replace('[btc]', btcG).replace('[diff]', diffG);
    }
    if (elCompBtcGrowth) elCompBtcGrowth.addEventListener('input', function() { updateForecastSummary(); updateHodlHint(); calculateComparison(); });
    if (elCompDiffGrowth) elCompDiffGrowth.addEventListener('input', function() { updateForecastSummary(); calculateComparison(); });
    // Custom price input
    if (elCompCustomPrice) {
      elCompCustomPrice.addEventListener('input', function() {
        var val = elCompCustomPrice.value;
        var cursorPos = elCompCustomPrice.selectionStart;
        var digitsBeforeCursor = val.slice(0, cursorPos).replace(/[^\d]/g, '').length;

        var parsed = parsePriceInput(val);
        if (parsed > 0) {
          var formatted = formatPriceForInput(parsed);
          elCompCustomPrice.value = formatted;

          // Restore cursor position based on digit count
          var newPos = 0;
          var digitCount = 0;
          for (var j = 0; j < formatted.length; j++) {
            if (/\d/.test(formatted[j])) {
              digitCount++;
              if (digitCount === digitsBeforeCursor) { newPos = j + 1; break; }
            }
          }
          if (digitCount < digitsBeforeCursor) newPos = formatted.length;
          elCompCustomPrice.setSelectionRange(newPos, newPos);
        }
        calculateComparison();
      });
      elCompCustomPrice.addEventListener('focus', function() { elCompCustomPrice.select(); });
    }

    // Timeframe pills
    if (elCompTimeframe) {
      elCompTimeframe.addEventListener('change', function() {
        var raw = elCompTimeframe.value;
        if (raw === 'custom') {
          if (elCompCustomTimeframe) elCompCustomTimeframe.hidden = false;
          var customVal = elCompCustomYears ? parseInt(elCompCustomYears.value, 10) : 10;
          compTimeframeYears = customVal > 0 ? customVal : 10;
        } else {
          if (elCompCustomTimeframe) elCompCustomTimeframe.hidden = true;
          compTimeframeYears = parseInt(raw, 10) || 4;
        }
        updateDifficultyHint();
        updateTimeframeHint();
        updateHalvingHint();
        updateHodlHint();
        calculateComparison();
      });
    }

    // Custom timeframe input
    if (elCompCustomYears) {
      elCompCustomYears.addEventListener('input', function() {
        var v = parseInt(elCompCustomYears.value, 10);
        if (v > 0) {
          compTimeframeYears = v;
          updateDifficultyHint();
          updateTimeframeHint();
          updateHalvingHint();
          updateHodlHint();
          calculateComparison();
        }
      });
    }

    // Product type dropdown (heaters / miners)
    if (elCompProductType) {
      elCompProductType.addEventListener('change', function() {
        var view = elCompProductType.value;
        // Save current sunshine hours before switching
        if (elCompPvSunshine) {
          if (compActiveView === 'heaters') {
            heaterSunshineHours = parseFloat(elCompPvSunshine.value) || 2;
          } else {
            minerSunshineHours = parseFloat(elCompPvSunshine.value) || 4;
          }
        }
        compActiveView = view;
        // Restore sunshine hours
        if (elCompPvSunshine) {
          elCompPvSunshine.value = (view === 'heaters') ? heaterSunshineHours : minerSunshineHours;
        }
        // Show/hide views
        if (elCompViewHeaters) elCompViewHeaters.hidden = (view !== 'heaters');
        if (elCompViewMiners) elCompViewMiners.hidden = (view !== 'miners');
        // Show/hide heating inputs (shown for heaters, hidden for miners)
        updateMinerHeatingVisibility();
        calculateComparison();
      });
    }

    // Miner hint link → switch to heaters
    var elMinerHintLink = document.getElementById('bmcMinerHintLink');
    if (elMinerHintLink && elCompProductType) {
      elMinerHintLink.addEventListener('click', function() {
        elCompProductType.value = 'heaters';
        elCompProductType.dispatchEvent(new Event('change'));
      });
    }

    setDefaultHeaterQuantities();
    updateTimeframeHint();
    updateHeatingMonthsHint();
    updateHeaterSeasonHint();
    updateHalvingHint();
    updateHodlHint();
    forecastEnabled = elCompForecastToggle ? elCompForecastToggle.checked : true;
    updateMinerHeatingVisibility();
    calculateComparison();
  }

  // Fetch APIs and initialize
  function init() {
    updateCurrencySuffix();
    initTooltips();
    initTabs();

    // Set initial currency dropdown to match active currency
    if (elCurrencySelect) {
      // If Shopify's active currency matches one of our options, select it
      var shopifyCurrency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || window.shopifyCurrencyCode || 'EUR';
      var optionExists = elCurrencySelect.querySelector('option[value="' + shopifyCurrency + '"]');
      if (optionExists) {
        elCurrencySelect.value = shopifyCurrency;
      }
      // Apply the selected currency config
      switchCurrency(elCurrencySelect.value);

      elCurrencySelect.addEventListener('change', function() {
        switchCurrency(elCurrencySelect.value);
      });
    }

    // Initialize comparison tab
    initComparison();

    // Populate device dropdown from compProducts (skip heaters), grouped by manufacturer
    if (elDevice) {
      var deviceGroups = {};
      var deviceGroupOrder = [];
      for (var di = 0; di < compProducts.length; di++) {
        var cp = compProducts[di];
        if (cp.isHeater) continue;
        if (cp.hashrate <= 0 || cp.power <= 0) continue;
        var group = cp.group || 'Other';
        if (!deviceGroups[group]) {
          deviceGroups[group] = [];
          deviceGroupOrder.push(group);
        }
        deviceGroups[group].push(cp);
      }
      deviceGroupOrder.sort();
      for (var gi = 0; gi < deviceGroupOrder.length; gi++) {
        var gKey = deviceGroupOrder[gi];
        var items = deviceGroups[gKey];
        items.sort(function(a, b) { return b.hashrate - a.hashrate; });
        var optgroup = document.createElement('optgroup');
        optgroup.label = gKey;
        for (var ii = 0; ii < items.length; ii++) {
          var opt = document.createElement('option');
          opt.value = items[ii].key + '|' + items[ii].hashrate + ':' + items[ii].power;
          opt.textContent = items[ii].name;
          optgroup.appendChild(opt);
        }
        elDevice.appendChild(optgroup);
      }

      // Default to S19k Pro 110 TH / Braiins OS
      var allOpts = elDevice.querySelectorAll('option');
      for (var oi = 0; oi < allOpts.length; oi++) {
        var optText = allOpts[oi].textContent.toLowerCase();
        if (optText.indexOf('s19k pro') !== -1 && optText.indexOf('110 th') !== -1 && optText.indexOf('braiins') !== -1) {
          elDevice.selectedIndex = oi;
          var defValParts = allOpts[oi].value.split('|');
          var defParts = (defValParts[1] || defValParts[0]).split(':');
          elHashrate.value = parseFloat(defParts[0]);
          elPower.value = parseFloat(defParts[1]);
          break;
        }
      }

      elDevice.addEventListener('change', function() {
        if (elDevice.value === 'custom') {
          elHashrate.value = 100;
          elPower.value = 1500;
          calculate();
          return;
        }
        var valParts = elDevice.value.split('|');
        var parts = (valParts[1] || valParts[0]).split(':');
        elHashrate.value = parseFloat(parts[0]);
        elPower.value = parseFloat(parts[1]);
        calculate();
      });
    }

    // Attach input listeners for the 4 mining parameter inputs
    var inputs = [elHashrate, elPower, elElectricity, elPoolFee];
    for (var i = 0; i < inputs.length; i++) {
      inputs[i].addEventListener('input', calculate);
    }

    // Switch device dropdown to "Custom" when hashrate or power is manually changed
    if (elDevice) {
      elHashrate.addEventListener('input', function() { elDevice.value = 'custom'; });
      elPower.addEventListener('input', function() { elDevice.value = 'custom'; });
    }

    // Mining tab PV toggle
    if (elPvToggle && elPvGroup) {
      elPvToggle.addEventListener('change', function() {
        toggleGroup(elPvToggle, elPvGroup);
        calculate();
      });
    }
    // Mining tab PV sub-field inputs
    if (elPvSize) elPvSize.addEventListener('input', calculate);
    if (elPvSunshine) elPvSunshine.addEventListener('input', calculate);
    if (elPvAllocation) elPvAllocation.addEventListener('input', calculate);
    if (elFeedInTariff) elFeedInTariff.addEventListener('input', calculate);

    // Price input: live formatting as you type
    elPriceInput.addEventListener('input', function() {
      var val = elPriceInput.value;
      var cursorPos = elPriceInput.selectionStart;
      var digitsBeforeCursor = val.slice(0, cursorPos).replace(/[^\d]/g, '').length;

      var parsed = parsePriceInput(val);
      if (parsed > 0) {
        btcPrice = parsed;
        var formatted = formatPriceForInput(parsed);
        elPriceInput.value = formatted;

        var newPos = 0;
        var digitCount = 0;
        for (var j = 0; j < formatted.length; j++) {
          if (/\d/.test(formatted[j])) {
            digitCount++;
            if (digitCount === digitsBeforeCursor) { newPos = j + 1; break; }
          }
        }
        if (digitCount < digitsBeforeCursor) newPos = formatted.length;
        elPriceInput.setSelectionRange(newPos, newPos);
      }
      autoSizePriceInput();
      updatePriceBadge();
      calculate();
    });

    // Price input: on Enter, blur to commit
    elPriceInput.addEventListener('keydown', function(e) {
      if (e.key === 'Enter') {
        elPriceInput.blur();
      }
    });

    // Price input: select all on focus for easy replacement
    elPriceInput.addEventListener('focus', function() {
      elPriceInput.select();
    });

    // Badge click: revert to live price when in custom mode
    elPriceBadge.addEventListener('click', function() {
      if (liveBtcPrice > 0 && elPriceBadge.classList.contains('bmc-calc__price-badge--custom')) {
        btcPrice = liveBtcPrice;
        elPriceInput.value = formatPriceForInput(btcPrice);
        autoSizePriceInput();
        updatePriceBadge();
        calculate();
      }
    });

    // Reset button: revert to live price
    elPriceReset.addEventListener('click', function() {
      if (liveBtcPrice > 0) {
        btcPrice = liveBtcPrice;
        elPriceInput.value = formatPriceForInput(btcPrice);
        autoSizePriceInput();
        updatePriceBadge();
        calculate();
      }
    });

    // View mode dropdown (projection / historical)
    var elViewMode = document.getElementById('bmcViewMode');
    if (elViewMode) {
      elViewMode.addEventListener('change', function() {
        var mode = elViewMode.value;
        if (elProjectionCards) elProjectionCards.hidden = (mode !== 'projection');
        if (elHistoricalCards) elHistoricalCards.hidden = (mode !== 'historical');
        var elPriceField = document.getElementById('bmcPriceField');
        if (elPriceField) elPriceField.hidden = (mode === 'historical');
      });
    }

    // Fetch BTC price and difficulty in parallel
    var pricePromise = fetch('https://api.blockchain.info/ticker')
      .then(function(res) { return res.json(); })
      .then(function(data) {
        tickerData = data;
        var price = getBtcPriceFromTicker(data);
        btcPrice = price;
        liveBtcPrice = price;
        if (price > 0) {
          elPriceInput.value = formatPriceForInput(price);
          autoSizePriceInput();
          updatePriceBadge();
        } else {
          elPriceInput.value = labels.error_loading || 'Error';
          autoSizePriceInput();
        }
      })
      .catch(function() {
        elPriceInput.value = labels.error_loading || 'Error';
        autoSizePriceInput();
      });

    var difficultyPromise = fetch('https://api.blockchain.info/q/getdifficulty')
      .then(function(res) { return res.text(); })
      .then(function(text) {
        difficulty = parseFloat(text);
        networkHashrateTH = difficulty * Math.pow(2, 32) / 600 / 1e12;
      })
      .catch(function() {
        difficulty = 0;
        networkHashrateTH = 0;
      });

    Promise.all([pricePromise, difficultyPromise]).then(function() {
      calculate();
      calculateComparison();
      updateDifficultyHint();
    });

    // Fetch historical data (non-blocking, runs in parallel)
    fetchHistoricalData();

    // Fetch 4-year price + difficulty data for average modes
    fetchFourYearData();
  }

  // Run on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
